// Industry landing page data — drives /:industrySlug pages via IndustryPage.jsx
// Each industry gets a full digital-marketing landing page with hero, insights,
// pillars, case study, tactics, process, FAQ and related industries.

export const INDUSTRY_LIST = [
  /* ─────────────────────────────── SHOPIFY & ECOMMERCE ─────────────────────────────── */
  {
    slug: "shopify-ecommerce",
    name: "Shopify & Ecommerce",
    shortName: "Ecommerce",
    emoji: "🛒",
    cover: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&q=85",
    group: "Ecommerce & Retail",
    desc: "Scale your store revenue with Google, Meta and email.",
    metaTitle: "Shopify & Ecommerce Marketing Agency — Scale Store Revenue with Google, Meta & AI | Lumotis",
    metaDescription: "Lumotis is a full-stack Shopify & ecommerce marketing agency. We scale store revenue with Google Shopping, Meta Ads, Klaviyo email, SEO and AI search visibility. Free audit.",
    heroEyebrow: "Ecommerce & Shopify Marketing",
    heroHeadline: "Ecommerce Marketing That Turns Browsers into Buyers",
    heroAccent: "More revenue. Lower CAC. Compounding growth.",
    heroTagline: "We run the complete revenue stack for Shopify and ecommerce brands — Google Shopping, Meta ROAS campaigns, Klaviyo flows, SEO and AI citation — all coordinated under one roof.",
    heroStats: [
      { value: "+340%", label: "Avg ROAS increase" },
      { value: "50+", label: "Stores scaled" },
      { value: "3.2×", label: "Average revenue multiplier" },
    ],
    insightStats: [
      {
        value: "76%",
        label: "of shoppers research online before buying",
        desc: "Your brand must appear across Google, Meta, ChatGPT and email before a buyer ever visits your store. Multi-channel presence is table stakes.",
      },
      {
        value: "4.5×",
        label: "higher LTV from email + SMS combined",
        desc: "A single well-built Klaviyo flow stack typically generates 30–45% of total store revenue — at near-zero cost per sale.",
      },
      {
        value: "62%",
        label: "of ecommerce traffic is now paid or email",
        desc: "Organic-only stores are exposed. A blended paid + SEO + email model inoculates revenue against algorithm changes.",
      },
    ],
    pillars: [
      {
        title: "Google Shopping & Performance Max",
        body: "We structure Shopping campaigns by product margin, not just revenue. Smart bidding tuned to profit — not vanity ROAS — with negative keyword hygiene and feed optimisation every sprint.",
      },
      {
        title: "Meta Ads (Facebook & Instagram)",
        body: "Prospecting with UGC-style creatives, retargeting with dynamic product ads and lookalike audiences built on your best buyers. Full creative production and copy in-house.",
      },
      {
        title: "Klaviyo Email & SMS Flows",
        body: "Abandon cart, post-purchase, win-back, VIP and browse-abandon sequences built and A/B tested until they hit industry-leading open rates. Most stores see 30–45% of revenue from email within 90 days.",
      },
      {
        title: "Ecommerce SEO",
        body: "Collection and product page keyword strategy, internal linking architecture, Core Web Vitals and schema markup. Organic search compounds forever — paid spend doesn't.",
      },
      {
        title: "AI Search Visibility (GEO/AEO)",
        body: "We structure your product and brand content so ChatGPT, Perplexity and Google AI Overviews recommend your store when buyers ask relevant questions. The new discovery channel most stores ignore.",
      },
      {
        title: "Conversion Rate Optimisation",
        body: "Heatmaps, session recordings, A/B tests on PDPs, cart and checkout pages. We don't just drive traffic — we make sure it converts before scaling spend.",
      },
    ],
    caseStudy: {
      metric: "+340%",
      metricLabel: "return on ad spend in 90 days",
      badge: "Ecommerce • USA • Google + Meta + Klaviyo",
      title: "US Fashion Brand — from break-even ads to a 4.4× blended ROAS.",
      body: "A US DTC apparel brand came to Lumotis with Google Ads at 1.8× ROAS and Meta barely breaking even. We restructured Google Shopping by margin tier, rebuilt Meta with UGC creative and launched a 5-flow Klaviyo sequence. 90 days later: blended ROAS 4.4×, email now drives 38% of total revenue, and CAC dropped 41%.",
    },
    results: [
      { metric: "+340%", label: "ROAS improvement in 90 days", market: "USA · Google + Meta" },
      { metric: "38%", label: "of revenue from email flows", market: "Klaviyo · Shopify" },
      { metric: "−41%", label: "customer acquisition cost", market: "DTC Apparel" },
      { metric: "3.2×", label: "total store revenue growth", market: "12-month engagement" },
    ],
    tactics: [
      "Google Shopping campaigns",
      "Performance Max structure",
      "Meta prospecting & retargeting",
      "UGC creative production",
      "Klaviyo email flows",
      "SMS abandonment",
      "Shopify SEO",
      "Product page CRO",
      "Dynamic remarketing",
      "Lookalike audiences",
      "Subscription upsells",
      "Influencer seeding",
      "Google Merchant Center feed",
      "AI Overview citations",
      "Post-purchase sequences",
      "VIP loyalty flows",
      "Collection page SEO",
      "Core Web Vitals",
      "Schema markup",
      "Win-back campaigns",
    ],
    faq: [
      {
        q: "How quickly will we see results from ecommerce marketing?",
        a: "Paid ads (Google Shopping, Meta) can show measurable ROAS improvement within 2–4 weeks of launch. Email flows typically deliver strong results within 30–60 days as sequences are built and tested. SEO and AI visibility compound over 3–6 months.",
      },
      {
        q: "Do you work with non-Shopify ecommerce platforms?",
        a: "Yes. While Shopify is our most common platform, we regularly work with WooCommerce, BigCommerce, and custom-built stores. The marketing stack — Google, Meta, Klaviyo, SEO — is platform-agnostic.",
      },
      {
        q: "What ad spend minimum do you recommend?",
        a: "For Google Shopping + Meta, we recommend a minimum of $3,000–$5,000/month in ad spend to generate statistically meaningful data for optimisation. Engagements below this threshold tend to learn too slowly to produce strong ROAS.",
      },
      {
        q: "Do you handle creative production for ads?",
        a: "Yes — our in-house team handles static, video and UGC-style creative. We brief, produce and A/B test all ad creative. For UGC, we either work with your existing customers or source creators from our network.",
      },
    ],
    relatedSlugs: ["fashion-apparel-marketing", "real-estate-marketing", "saas-marketing"],
  },

  /* ─────────────────────────────── FASHION & APPAREL ─────────────────────────────── */
  {
    slug: "fashion-apparel-marketing",
    name: "Fashion & Apparel",
    shortName: "Fashion",
    emoji: "👗",
    cover: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=85",
    group: "Ecommerce & Retail",
    desc: "Grow your fashion brand with Meta, influencers and SEO.",
    metaTitle: "Fashion & Apparel Marketing Agency — Grow Your Brand with Meta, Influencers & SEO | Lumotis",
    metaDescription: "Lumotis is a specialist fashion and apparel marketing agency. We scale clothing brands with Meta Ads, influencer campaigns, Google Shopping, email flows and AI brand visibility. Free audit.",
    heroEyebrow: "Digital Marketing for Fashion & Apparel",
    heroHeadline: "Fashion Marketing That Builds Brands and Sells Collections Fast",
    heroAccent: "More sell-throughs. Stronger brand. Loyal repeat buyers.",
    heroTagline: "We power the full marketing engine for fashion and apparel brands — from Meta prospecting and influencer seeding to Google Shopping, Klaviyo retention and AI brand citations — turning browsers into brand loyalists.",
    heroStats: [
      { value: "+290%", label: "Avg Meta ROAS increase" },
      { value: "35+", label: "Fashion brands scaled" },
      { value: "42%", label: "Higher repeat purchase rate" },
    ],
    insightStats: [
      {
        value: "74%",
        label: "of fashion purchases are influenced by social media",
        desc: "Instagram, TikTok and Pinterest are where fashion discovery happens. Brands that invest in consistent visual storytelling across these platforms win the consideration battle before the purchase decision.",
      },
      {
        value: "3.8×",
        label: "higher conversion rate with UGC vs brand creative",
        desc: "User-generated content and creator partnerships consistently outperform polished brand ads in fashion. Authenticity drives clicks — and purchases.",
      },
      {
        value: "55%",
        label: "of fashion revenue comes from repeat customers",
        desc: "Post-purchase email flows, seasonal drop announcements and loyalty programmes are the highest-margin revenue lever for any fashion brand. Most brands underinvest here.",
      },
    ],
    pillars: [
      {
        title: "Meta & Instagram Ads",
        body: "Prospecting campaigns with Reels and carousel creative, dynamic product retargeting and lookalike audiences built on your highest-LTV buyers. We manage creative iteration weekly to prevent ad fatigue.",
      },
      {
        title: "Influencer & Creator Campaigns",
        body: "Micro and mid-tier influencer seeding, UGC creator briefs and paid whitelisting campaigns. We source, brief, manage and measure every creator partnership — no guesswork on ROI.",
      },
      {
        title: "Google Shopping & Performance Max",
        body: "Margin-structured Shopping campaigns with expert product feed optimisation, negative keyword hygiene and bidding strategies calibrated to your sell-through goals and seasonal drops.",
      },
      {
        title: "Klaviyo Email & SMS Retention",
        body: "New arrival announcements, abandoned cart flows, back-in-stock alerts, VIP early access sequences and win-back campaigns. Built and tested until email drives 35–45% of total revenue.",
      },
      {
        title: "TikTok & Pinterest Advertising",
        body: "TikTok Spark Ads boosting top organic content, shopping catalogue campaigns and Pinterest visual search ads — capturing fashion discovery intent across every platform your customers browse.",
      },
      {
        title: "Fashion SEO & AI Brand Visibility",
        body: "Category page SEO targeting style keywords, trend-based blog content and structured data that gets your brand cited in ChatGPT and Google AI Overviews when shoppers ask fashion questions.",
      },
    ],
    caseStudy: {
      metric: "+290%",
      metricLabel: "Meta ROAS in 60 days",
      badge: "Fashion · UK DTC · Meta + Influencers + Klaviyo",
      title: "UK Womenswear Brand — from 1.9× ROAS to 5.5× with a full creator stack.",
      body: "A UK DTC womenswear brand had strong product-market fit but Meta ads at 1.9× ROAS and no influencer strategy. Lumotis rebuilt their Meta creative stack with UGC, launched 12 micro-influencer partnerships and deployed a 6-flow Klaviyo retention system. 60 days later: Meta ROAS at 5.5×, email contributing 41% of revenue, and influencer content reduced CPMs by 38%.",
    },
    results: [
      { metric: "+290%", label: "Meta ROAS improvement", market: "UK · Womenswear" },
      { metric: "41%", label: "of revenue from email", market: "Klaviyo · DTC" },
      { metric: "−38%", label: "CPM reduction via UGC", market: "Influencer whitelisting" },
      { metric: "12×", label: "influencer partnerships managed", market: "Micro-creator network" },
    ],
    tactics: [
      "Meta prospecting campaigns",
      "Instagram Reels ads",
      "Dynamic product retargeting",
      "Lookalike audience building",
      "Micro-influencer seeding",
      "UGC creator briefs",
      "Paid whitelisting campaigns",
      "Google Shopping – margin structure",
      "Product feed optimisation",
      "Klaviyo welcome series",
      "Abandoned cart flows",
      "Back-in-stock alerts",
      "VIP early-access sequences",
      "TikTok Spark Ads",
      "Pinterest Shopping campaigns",
      "Fashion SEO – category pages",
      "Trend blog content",
      "Win-back email campaigns",
      "Seasonal drop campaigns",
      "AI brand citation building",
    ],
    faq: [
      {
        q: "Do you work with emerging fashion brands or only established labels?",
        a: "Both. We have specific playbooks for early-stage brands building their first paid channels from scratch, and for established brands scaling past plateaus. Minimum ad spend of $2,000/month is recommended for emerging brands.",
      },
      {
        q: "How do you approach influencer campaigns for fashion?",
        a: "We identify micro and mid-tier creators (10k–500k followers) whose audience demographics match your target buyer. We handle all outreach, briefing, content approval and performance reporting. Paid whitelisting of top-performing content amplifies reach without additional creator fees.",
      },
      {
        q: "Can you help with seasonal campaign planning — like pre-launch and sale events?",
        a: "Yes. Seasonal planning is built into our fashion marketing retainer. We plan 8–12 weeks ahead for major launches, sale events and new drop announcements — ensuring creative, email and paid are all coordinated.",
      },
      {
        q: "What platforms do you specialise in for fashion?",
        a: "Meta (Instagram + Facebook), TikTok, Pinterest and Google Shopping are our primary channels. We also run YouTube pre-roll for brand-awareness campaigns for fashion brands with larger budgets.",
      },
    ],
    relatedSlugs: ["shopify-ecommerce", "wedding-events-marketing", "photography-marketing"],
  },

  /* ─────────────────────────────── REAL ESTATE ─────────────────────────────── */
  {
    slug: "real-estate-marketing",
    name: "Real Estate Marketing",
    shortName: "Real Estate",
    emoji: "🏡",
    cover: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=85",
    group: "Real Estate & Property",
    desc: "Generate qualified buyer and seller leads on autopilot.",
    metaTitle: "Real Estate Marketing Agency — Qualified Leads, Local SEO & Automation | Lumotis",
    metaDescription: "Lumotis is a specialist real estate marketing agency. We generate qualified buyer and seller leads with Google Ads, Facebook, Local SEO, landing pages and CRM automation. Free audit.",
    heroEyebrow: "Digital Marketing for Real Estate",
    heroHeadline: "Real Estate Marketing That Fills Your Pipeline with Qualified Leads",
    heroAccent: "More listings. More buyers. Less time wasted on tyre-kickers.",
    heroTagline: "We build the full lead generation engine for real estate agents, brokers and property developers — Google Ads, Facebook, Local SEO, landing pages and CRM automation — so you close more deals without chasing cold leads.",
    heroStats: [
      { value: "3×", label: "Lead volume increase" },
      { value: "$2.1M+", label: "Revenue generated for clients" },
      { value: "41%", label: "Lower cost per qualified lead" },
    ],
    insightStats: [
      {
        value: "97%",
        label: "of homebuyers search online first",
        desc: "Your Google Business Profile, website and paid ads are seen before you ever pick up a phone. Digital presence determines whether buyers call you or your competitor.",
      },
      {
        value: "3.5×",
        label: "more leads from Google Local Services Ads vs standard search",
        desc: "LSAs show your verified badge, reviews and phone number at the very top of Google — above organic and standard paid results. Most agents ignore this channel entirely.",
      },
      {
        value: "60%",
        label: "of real estate leads go cold if not contacted within 1 hour",
        desc: "Speed-to-lead is the biggest conversion lever in real estate. We build automated follow-up sequences that respond within 60 seconds — 24/7.",
      },
    ],
    pillars: [
      {
        title: "Google & Facebook Lead Generation",
        body: "Intent-based Google Search campaigns targeting buyer and seller keywords, plus Facebook Lead Ads with custom audiences built on ZIP code, income and life-event targeting. Every campaign is built around cost-per-qualified-lead, not vanity clicks.",
      },
      {
        title: "Local SEO & Google Business Profile",
        body: "Rank #1 for '[city] real estate agent', '[neighbourhood] homes for sale' and every 'near me' search in your farm area. We optimise your GBP, build local citations and generate review velocity.",
      },
      {
        title: "Landing Pages & Lead Funnels",
        body: "High-converting landing pages built for each campaign — buyer funnels, seller funnels, free home valuation pages and open-house capture pages. A/B tested until cost-per-lead hits target.",
      },
      {
        title: "CRM Automation & Drip Sequences",
        body: "Speed-to-lead automation that responds within 60 seconds of form submission. Multi-touch email + SMS drip sequences that nurture cold leads into ready buyers over 30–180 days.",
      },
      {
        title: "AI Search Visibility (GEO/AEO)",
        body: "We structure your content so when buyers ask ChatGPT or Perplexity 'best real estate agent in [city]', your name comes up. The new referral channel most agents haven't discovered yet.",
      },
      {
        title: "Video & Social Media Content",
        body: "Property tour Reels, neighbourhood feature videos and market update content that builds your personal brand and keeps you top-of-mind with your database — every single week.",
      },
    ],
    caseStudy: {
      metric: "3×",
      metricLabel: "qualified leads per month",
      badge: "Real Estate • Dubai · Google + Facebook + Local SEO",
      title: "Dubai Property Developer — from 40 leads/month to 127 qualified enquiries.",
      body: "A Dubai-based property developer was buying low-quality leads from aggregator portals and converting below 2%. Lumotis built a full-funnel Google + Facebook system with dedicated landing pages and a HubSpot CRM automation sequence. 90-day result: 127 qualified leads per month (up from 40), cost-per-qualified-lead down 41%, and conversion-to-viewing up to 18%.",
    },
    results: [
      { metric: "3×", label: "qualified leads per month", market: "Dubai · Property" },
      { metric: "−41%", label: "cost per qualified lead", market: "Google + Facebook" },
      { metric: "18%", label: "lead-to-viewing conversion", market: "HubSpot automation" },
      { metric: "#1", label: "Google Maps ranking", market: "Local SEO · 12 keywords" },
    ],
    tactics: [
      "Google Search – buyer intent",
      "Google Search – seller intent",
      "Google Local Services Ads",
      "Facebook Lead Ads",
      "Instagram property Reels",
      "Home valuation landing page",
      "Buyer funnel pages",
      "Speed-to-lead automation",
      "HubSpot / Go High Level CRM",
      "Email drip sequences",
      "SMS follow-up flows",
      "Local SEO – city keywords",
      "Google Business Profile optimisation",
      "Neighbourhood content pages",
      "Review generation campaigns",
      "AI Overview citations",
      "YouTube property tours",
      "Retargeting past visitors",
      "Lookalike audiences",
      "Open-house capture funnels",
    ],
    faq: [
      {
        q: "How long does it take to see real estate leads from digital marketing?",
        a: "Google and Facebook lead campaigns typically generate enquiries within 7–14 days of launch. Local SEO builds over 3–6 months. CRM automation activates from day one — meaning every lead, even slow ones, gets followed up automatically.",
      },
      {
        q: "Do you work with individual agents or only brokers and developers?",
        a: "Both. We have tailored packages for solo agents building a personal brand, small brokerages scaling their team's pipeline, and large property developers running multi-project campaigns simultaneously.",
      },
      {
        q: "What CRM platforms do you integrate with?",
        a: "We build automations on Go High Level, HubSpot, Follow Up Boss, and kvCORE — the most common real estate CRMs. If you use a different platform, we'll assess it during onboarding.",
      },
      {
        q: "Can you help with new development project launches?",
        a: "Yes. Project launches are a specific speciality — we build pre-launch waitlist funnels, interest registration campaigns, VIP early-access sequences, and launch-day paid campaigns that fill appointment calendars fast.",
      },
    ],
    relatedSlugs: ["mortgage-broker-marketing", "construction-marketing", "financial-advisor-marketing"],
  },

  /* ─────────────────────────────── MORTGAGE & FINANCE BROKERS ─────────────────────────────── */
  {
    slug: "mortgage-broker-marketing",
    name: "Mortgage & Finance Brokers",
    shortName: "Mortgage",
    emoji: "🏦",
    cover: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=85",
    group: "Real Estate & Property",
    desc: "Fill your mortgage pipeline with pre-qualified enquiries.",
    metaTitle: "Mortgage Broker Marketing Agency — Pre-Qualified Leads & Google Ads | Lumotis",
    metaDescription: "Lumotis is a specialist mortgage broker marketing agency. We fill your pipeline with pre-qualified leads using Google Ads, Facebook, Local SEO and CRM automation. Free audit.",
    heroEyebrow: "Digital Marketing for Mortgage Brokers",
    heroHeadline: "Mortgage Marketing That Delivers Pre-Qualified Borrowers to Your Desk",
    heroAccent: "More settled loans. Lower cost-per-lead. Automated follow-up.",
    heroTagline: "We build the complete lead generation and nurture engine for mortgage brokers and finance companies — Google Ads, Facebook, Local SEO, pre-qualification funnels and CRM automation — so your pipeline never runs dry.",
    heroStats: [
      { value: "+220%", label: "Avg lead volume increase" },
      { value: "45+", label: "Brokers and lenders served" },
      { value: "−38%", label: "Cost per qualified application" },
    ],
    insightStats: [
      {
        value: "82%",
        label: "of borrowers compare 3+ lenders online before choosing",
        desc: "Visibility across Google Search, comparison sites and social media is essential. Brokers who appear first — and look most trustworthy — win the majority of enquiries.",
      },
      {
        value: "4.2×",
        label: "higher application rate from pre-qualification funnels vs raw enquiry forms",
        desc: "A well-built pre-qualification funnel filters tyre-kickers, segments borrowers by loan size and improves conversion from lead to application significantly.",
      },
      {
        value: "70%",
        label: "of mortgage leads require 3–6 months of nurture before settling",
        desc: "Most mortgage leads are not ready today. Long-cycle email and SMS nurture sequences keep your brand top-of-mind until the borrower is ready — most brokers never build this.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Mortgage Leads",
        body: "High-intent search campaigns targeting purchase, refinance and investment loan keywords. Pre-qualification landing pages that filter leads by loan size and readiness. Bidding optimised for cost-per-qualified-application.",
      },
      {
        title: "Facebook & Instagram Lead Campaigns",
        body: "Targeted lead generation campaigns built on homeowner demographics, life events (new job, marriage, new baby) and interest-based audiences. Instant Form and landing page variants tested for lowest CPL.",
      },
      {
        title: "Local SEO & Google Business Profile",
        body: "Rank for '[city] mortgage broker', 'home loan broker near me' and refinance keywords in your market. We optimise your GBP, build authority content and establish you as the go-to local lending expert.",
      },
      {
        title: "Pre-Qualification Funnels",
        body: "Multi-step landing pages that qualify borrowers by loan purpose, property value, income and credit profile — before they ever speak to you. Improves lead quality dramatically and saves your time.",
      },
      {
        title: "CRM Nurture & Long-Cycle Automation",
        body: "Email and SMS drip sequences that keep your brand front-of-mind for 3–12 months. Rate update alerts, market news and helpful buying guides that build trust until the borrower is ready to act.",
      },
      {
        title: "Review & Referral Generation",
        body: "Post-settlement review requests, referral incentive campaigns and Google Business Profile optimisation that builds a reputation strong enough to generate word-of-mouth leads at scale.",
      },
    ],
    caseStudy: {
      metric: "+220%",
      metricLabel: "qualified leads per month in 90 days",
      badge: "Mortgage · Australia · Google + Facebook + Pre-Qual Funnel",
      title: "Sydney Mortgage Broker — from 15 to 48 qualified applications per month.",
      body: "A Sydney mortgage broker was generating 15 leads per month from a basic website with no paid campaigns. Lumotis built Google Ads targeting refinance and first-home-buyer keywords, a multi-step pre-qualification funnel and a 6-month email nurture sequence. 90 days in: 48 qualified applications per month, cost-per-application down 38%, and a 22% increase in settlement conversion rate.",
    },
    results: [
      { metric: "+220%", label: "qualified applications per month", market: "Sydney · Mortgage" },
      { metric: "−38%", label: "cost per qualified application", market: "Google + Facebook" },
      { metric: "+22%", label: "settlement conversion rate", market: "Pre-qual funnel" },
      { metric: "6mo", label: "average nurture sequence length", market: "Email + SMS" },
    ],
    tactics: [
      "Google Search – purchase intent",
      "Google Search – refinance intent",
      "Facebook Lead Ads",
      "Life-event audience targeting",
      "Multi-step pre-qualification funnel",
      "Landing page A/B testing",
      "Local SEO – broker keywords",
      "Google Business Profile",
      "Rate update email campaigns",
      "Long-cycle SMS nurture",
      "First-home-buyer guide content",
      "Refinance calculator landing page",
      "Investment loan campaigns",
      "Review generation post-settlement",
      "Referral incentive campaigns",
      "CRM automation (Go High Level)",
      "Retargeting website visitors",
      "AI Overview citations",
      "YouTube explainer ads",
      "Comparison site optimisation",
    ],
    faq: [
      {
        q: "How do you ensure the leads you generate are actually qualified?",
        a: "We use multi-step pre-qualification funnels that ask borrowers about loan purpose, property value, income and timeline before capturing contact details. This significantly filters tyre-kickers and improves the quality of every lead passed to you.",
      },
      {
        q: "Do you work with individual brokers or brokerage groups?",
        a: "Both. Solo brokers benefit from our personal brand and Google Ads approach. Larger brokerage groups can run multi-advisor campaigns with lead routing and territory-based attribution.",
      },
      {
        q: "Are your ads compliant with financial services advertising regulations?",
        a: "Yes. All ad copy includes required disclaimers, comparison rate information where relevant, and we follow ASIC guidelines (Australia), FCA rules (UK) and equivalent regulations in each market we operate.",
      },
      {
        q: "How long does it take to start receiving leads?",
        a: "Google Ads typically generates enquiries within 5–10 days of launch. Facebook campaigns often see leads within 24–48 hours of going live. Local SEO builds ranking over 3–5 months. CRM nurture activates from day one.",
      },
    ],
    relatedSlugs: ["real-estate-marketing", "financial-advisor-marketing", "accounting-marketing"],
  },

  /* ─────────────────────────────── HEALTHCARE ─────────────────────────────── */
  {
    slug: "healthcare-marketing",
    name: "Healthcare Marketing",
    shortName: "Healthcare",
    emoji: "🏥",
    cover: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=85",
    group: "Health & Medical",
    desc: "Grow patient numbers with compliant, high-trust marketing.",
    metaTitle: "Healthcare Marketing Agency — Patient Acquisition, Local SEO & Reputation | Lumotis",
    metaDescription: "Lumotis is a specialist healthcare digital marketing agency. We grow patient numbers with Google Ads, Local SEO, reputation management and HIPAA-compliant automation. Free audit.",
    heroEyebrow: "Digital Marketing for Healthcare",
    heroHeadline: "Healthcare Marketing That Grows Your Patient Base and Protects Your Reputation",
    heroAccent: "More new patients. Stronger authority. Fully compliant.",
    heroTagline: "We build compliant, performance-driven marketing systems for clinics, hospitals, private practices and healthcare networks — driving new patient acquisition, improving online reputation and establishing your practitioners as trusted local authorities.",
    heroStats: [
      { value: "+85%", label: "Avg new patient growth" },
      { value: "4.8★", label: "Average review rating achieved" },
      { value: "60+", label: "Healthcare practices served" },
    ],
    insightStats: [
      {
        value: "77%",
        label: "of patients use search to find a healthcare provider",
        desc: "Being visible on Google — both in paid ads and local organic — is the most reliable way to attract new patients. Referral networks alone no longer fill appointment books.",
      },
      {
        value: "84%",
        label: "of patients trust online reviews as much as personal recommendations",
        desc: "A strong review profile on Google, Healthgrades and Zocdoc is now a prerequisite for patient trust. One negative review left unanswered can cost you dozens of potential patients.",
      },
      {
        value: "2.5×",
        label: "higher patient lifetime value with retention marketing",
        desc: "Appointment reminder sequences, recall campaigns and health education emails keep existing patients engaged — reducing no-shows and increasing lifetime visit frequency significantly.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Patient Acquisition",
        body: "High-intent search campaigns targeting patients actively searching for your specialties. HIPAA-compliant ad copy, call tracking and landing pages built specifically for healthcare conversion. No generic healthcare templates.",
      },
      {
        title: "Local SEO & Google Business Profile",
        body: "Rank #1 for '[specialty] near me', '[city] [clinic type]' and every condition-specific search in your area. We optimise your GBP, build medical-grade local citations and manage your NAP consistency across all directories.",
      },
      {
        title: "Online Reputation Management",
        body: "Systematic review generation on Google, Healthgrades and Zocdoc. Prompt, professional replies to all reviews. Proactive monitoring and crisis response protocols. Most practices reach 4.7–4.9★ within 90 days.",
      },
      {
        title: "Content Marketing & Medical SEO",
        body: "Condition pages, treatment guides, FAQ content and practitioner profiles that rank for high-intent medical search queries — establishing your practice as the authoritative local source.",
      },
      {
        title: "Patient Retention & Recall Automation",
        body: "Appointment reminders, post-visit follow-ups, annual recall campaigns and health education email sequences that keep patients engaged, reduce no-shows and increase lifetime visit frequency.",
      },
      {
        title: "AI Search Visibility (GEO/AEO)",
        body: "When patients ask ChatGPT or Google AI Overviews about symptoms, treatments or local doctors, your practice and practitioners should be cited as authoritative sources. We build that presence.",
      },
    ],
    caseStudy: {
      metric: "+85%",
      metricLabel: "new patients per month",
      badge: "Healthcare · Private Dental Clinic · UK · Google + Local SEO",
      title: "UK Dental Practice — from 18 new patients/month to 33 in six months.",
      body: "A London private dental practice was relying entirely on word of mouth with an outdated website and no digital marketing. Lumotis launched Google Ads for high-value cosmetic treatments, rebuilt their GBP with weekly photo updates and posts, and deployed an automated post-treatment SMS review sequence. Six months later: 33 new patients/month, 4.9★ on Google, and cosmetic treatment enquiries up 3× with a 6-week consultation waitlist.",
    },
    results: [
      { metric: "+85%", label: "new patients per month", market: "London · Dental" },
      { metric: "4.9★", label: "Google review average", market: "Up from 4.1★" },
      { metric: "3×", label: "cosmetic treatment enquiries", market: "Google Ads" },
      { metric: "6 wks", label: "consultation waiting list", market: "High-value services" },
    ],
    tactics: [
      "Google Ads – specialty keywords",
      "Google Local Services Ads",
      "Local SEO – condition searches",
      "Google Business Profile",
      "Healthgrades optimisation",
      "Zocdoc profile management",
      "Review generation SMS",
      "Reputation monitoring",
      "Condition landing pages",
      "Practitioner authority content",
      "Medical FAQ SEO content",
      "Patient recall email sequences",
      "Appointment reminder SMS",
      "Post-visit follow-up flows",
      "HIPAA-compliant automation",
      "YouTube health education",
      "Facebook health campaigns",
      "AI Overview health citations",
      "Medical schema markup",
      "Trust page optimisation",
    ],
    faq: [
      {
        q: "Are your marketing strategies HIPAA compliant?",
        a: "Yes. All patient data handling, automation platforms and ad tracking are configured to be HIPAA compliant. We use compliant CRM and email platforms, avoid PHI in remarketing audiences, and follow BAA protocols where required.",
      },
      {
        q: "How long to see new patient growth from digital marketing?",
        a: "Google Ads can drive new patient enquiries within 1–2 weeks. Local SEO builds meaningful ranking improvement over 3–5 months. Reputation management typically shows measurable rating improvement within 30–60 days of systematic review generation.",
      },
      {
        q: "Do you work with all healthcare specialties?",
        a: "Yes. We work across dental, physiotherapy, chiropractic, cosmetic surgery, dermatology, ophthalmology, mental health, general practice and specialist clinics. Each specialty gets a tailored keyword and content strategy.",
      },
      {
        q: "Can you help a multi-location practice?",
        a: "Absolutely. Multi-location practices benefit significantly from our Local SEO approach — each location gets its own optimised GBP, location-specific landing pages and local citation profiles. We manage all locations under one coordinated strategy.",
      },
    ],
    relatedSlugs: ["dental-marketing", "chiropractic-marketing", "mental-health-marketing"],
  },

  /* ─────────────────────────────── DENTAL & ORTHODONTICS ─────────────────────────────── */
  {
    slug: "dental-marketing",
    name: "Dental & Orthodontics",
    shortName: "Dental",
    emoji: "🦷",
    cover: "https://images.unsplash.com/photo-1588776814546-1ffbb172f8de?w=1200&q=85",
    group: "Health & Medical",
    desc: "Attract high-value dental patients with targeted campaigns.",
    metaTitle: "Dental Marketing Agency — Attract New Patients with Google Ads & Local SEO | Lumotis",
    metaDescription: "Lumotis is a specialist dental marketing agency. We attract new patients for general dentists, orthodontists and cosmetic dental practices with Google Ads, Local SEO and reputation management. Free audit.",
    heroEyebrow: "Digital Marketing for Dental Practices",
    heroHeadline: "Dental Marketing That Fills Your Chair With High-Value Patients",
    heroAccent: "More new patients. More cosmetic cases. Zero cold calls.",
    heroTagline: "We build performance-driven marketing systems for dental practices — Google Ads targeting high-value treatments, Local SEO, review generation and patient recall automation — so your chair is always full and your revenue mix improves.",
    heroStats: [
      { value: "+120%", label: "Avg new patient increase" },
      { value: "80+", label: "Dental practices served" },
      { value: "$850", label: "Avg new patient value generated" },
    ],
    insightStats: [
      {
        value: "72%",
        label: "of patients choose the dentist with the most and best reviews",
        desc: "In a crowded dental market, your Google review profile is the primary trust signal. Practices with 100+ reviews and 4.8★+ consistently outperform competitors regardless of ad spend.",
      },
      {
        value: "$3,200",
        label: "average lifetime value of a new dental patient",
        desc: "Every new patient acquired is not just worth one appointment — they're worth years of check-ups, whitening, crowns and referrals. Investing in acquisition has exceptional long-term ROI.",
      },
      {
        value: "5×",
        label: "more enquiries for cosmetic dentistry from Google vs social media",
        desc: "High-intent Google searchers — people actively searching 'veneers near me' or 'Invisalign cost' — convert at dramatically higher rates than social media audiences for high-ticket dental treatments.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for High-Value Treatments",
        body: "Search campaigns targeting Invisalign, veneers, implants, whitening and cosmetic dentistry keywords. Landing pages built for each treatment with before/after imagery, pricing guides and instant consultation booking.",
      },
      {
        title: "Local SEO & Google Maps",
        body: "Rank #1 for '[city] dentist', 'dental implants near me', 'orthodontist [suburb]' and every treatment-specific search in your catchment area. GBP optimisation with weekly posts and fresh clinical photography.",
      },
      {
        title: "Reputation Management & Reviews",
        body: "Automated post-appointment SMS review requests, professional reply management and monitoring across Google, Healthgrades and RateMDs. Most practices reach 4.8–5.0★ within 90 days.",
      },
      {
        title: "Treatment-Specific Landing Pages",
        body: "Dedicated landing pages for each high-value treatment — Invisalign, implants, veneers, general check-up — each with strong CTAs, trust signals and conversion-optimised forms for consultation bookings.",
      },
      {
        title: "Patient Recall & Retention Automation",
        body: "6-month check-up reminders, hygiene recall SMS campaigns, post-treatment care sequences and win-back campaigns for lapsed patients. Reduces no-shows and maximises lifetime patient value.",
      },
      {
        title: "Social Media & Before/After Content",
        body: "HIPAA-compliant before/after content strategies, patient testimonial videos and educational dental content for Instagram and Facebook that builds trust and drives direct appointment enquiries.",
      },
    ],
    caseStudy: {
      metric: "+120%",
      metricLabel: "new patients per month in 4 months",
      badge: "Dental · Melbourne · Google Ads + Local SEO + Review Automation",
      title: "Melbourne Cosmetic Dentist — doubled new patients with targeted Invisalign campaigns.",
      body: "A Melbourne cosmetic dental practice wanted to grow their Invisalign and veneer caseload but had no digital marketing. Lumotis launched treatment-specific Google Ads for Invisalign and implants, rebuilt their GBP with clinical photography and deployed post-appointment SMS review automation. 4-month result: new patients up 120%, 4.9★ from 180 reviews, and Invisalign enquiries specifically up 210%.",
    },
    results: [
      { metric: "+120%", label: "new patients per month", market: "Melbourne · Cosmetic" },
      { metric: "+210%", label: "Invisalign enquiries", market: "Google Ads – treatment" },
      { metric: "4.9★", label: "Google review average", market: "180 reviews" },
      { metric: "#1", label: "Google Maps – 'cosmetic dentist Melbourne'", market: "Local SEO" },
    ],
    tactics: [
      "Google Ads – Invisalign campaigns",
      "Google Ads – dental implants",
      "Google Ads – veneers & cosmetic",
      "Local SEO – treatment keywords",
      "Google Business Profile weekly posts",
      "Clinical photography optimisation",
      "Post-appointment review SMS",
      "Healthgrades profile management",
      "Invisalign landing page",
      "Implant consultation funnel",
      "Before/after content strategy",
      "Facebook retargeting",
      "Instagram patient testimonials",
      "6-month recall SMS campaigns",
      "Hygiene reminder automation",
      "Lapsed patient win-back",
      "Consultation booking CRO",
      "AI Overview dental citations",
      "Schema markup – dental",
      "YouTube treatment education",
    ],
    faq: [
      {
        q: "Do you specialise in cosmetic dentistry marketing or general practices?",
        a: "Both. For cosmetic practices, we focus heavily on high-value treatment campaigns (Invisalign, veneers, implants) with dedicated landing pages. For general practices, we drive check-up and family dentistry volume alongside any cosmetic services offered.",
      },
      {
        q: "How do you handle before/after content compliantly?",
        a: "All before/after content is produced with explicit written patient consent and follows platform advertising policies and relevant medical advertising guidelines (AHPRA in Australia, GMC in UK, etc.). We brief you on consent documentation requirements during onboarding.",
      },
      {
        q: "Can you help us compete against large corporate dental chains?",
        a: "Yes — this is a common scenario. Independent practices win on personalisation, reputation and local trust. Our Local SEO and review strategy consistently helps independent practices outrank corporate chains on Google Maps, even with smaller budgets.",
      },
      {
        q: "Do you integrate with dental practice management software?",
        a: "We integrate with Dentrix, Eaglesoft, Curve Dental and most major PMS platforms for recall automation and appointment-based review triggers. If you use a different system, we'll assess it during onboarding.",
      },
    ],
    relatedSlugs: ["healthcare-marketing", "chiropractic-marketing", "cosmetic-surgery-marketing"],
  },

  /* ─────────────────────────────── CHIROPRACTIC & PHYSIO ─────────────────────────────── */
  {
    slug: "chiropractic-marketing",
    name: "Chiropractic & Physiotherapy",
    shortName: "Chiro & Physio",
    emoji: "🦴",
    cover: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&q=85",
    group: "Health & Medical",
    desc: "Book more appointments with local SEO and Google Ads.",
    metaTitle: "Chiropractic & Physiotherapy Marketing Agency — More Patients, Better Reviews | Lumotis",
    metaDescription: "Lumotis is a specialist chiropractic and physiotherapy marketing agency. We fill your appointment book with Google Ads, Local SEO, review generation and patient retention automation. Free audit.",
    heroEyebrow: "Digital Marketing for Chiropractors & Physios",
    heroHeadline: "Chiropractic Marketing That Keeps Your Treatment Rooms Fully Booked",
    heroAccent: "More new patients. Higher retention. A reputation that dominates locally.",
    heroTagline: "We build the complete patient acquisition engine for chiropractors, physiotherapists and allied health clinics — Google Ads, Local SEO, reputation management and retention automation — so your schedule is always full.",
    heroStats: [
      { value: "+95%", label: "Avg new patient increase" },
      { value: "70+", label: "Allied health clinics served" },
      { value: "4.8★", label: "Average review rating achieved" },
    ],
    insightStats: [
      {
        value: "68%",
        label: "of chiropractic patients find their practitioner via Google",
        desc: "Google Search and Google Maps are the dominant discovery channels for musculoskeletal care. Visibility in the local pack is the single highest-leverage investment for most chiro and physio clinics.",
      },
      {
        value: "6×",
        label: "higher ROI from Google Ads vs print or radio for allied health",
        desc: "High-intent paid search targets people actively searching for pain relief right now — dramatically outperforming broad awareness channels that reach passive audiences.",
      },
      {
        value: "40%",
        label: "of new patients come from referrals — which reviews amplify digitally",
        desc: "Online reviews are the digital equivalent of word-of-mouth. A practitioner with 150+ Google reviews and 4.8★ generates significantly more referral-equivalent trust than one with 20 reviews.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for New Patients",
        body: "Search campaigns targeting back pain, neck pain, sports injury, sciatica and condition-specific keywords with high purchase intent. Landing pages with trust signals, practitioner profiles and easy online booking.",
      },
      {
        title: "Local SEO & Google Maps",
        body: "Rank #1 for 'chiropractor near me', 'physiotherapist [suburb]' and every condition search in your catchment. We optimise your GBP, manage your citation profile and generate consistent review velocity.",
      },
      {
        title: "Reputation Management",
        body: "Automated post-appointment review requests, professional reply management and platform monitoring across Google and Healthgrades. Most clinics go from 3.9★ to 4.8★ within 60–90 days.",
      },
      {
        title: "Condition-Specific Content & SEO",
        body: "Dedicated pages for each condition you treat — lower back pain, sciatica, sports injuries, headaches — that rank for symptom-based searches and establish your practitioners as local subject-matter experts.",
      },
      {
        title: "Patient Retention & Care Plan Campaigns",
        body: "Appointment reminder sequences, follow-up check-in emails, care plan completion campaigns and lapsed patient win-back SMS — maximising retention and reducing single-visit drop-off.",
      },
      {
        title: "Social Media & Educational Content",
        body: "Weekly educational content — exercise tips, posture advice, injury prevention guides — for Instagram and Facebook that keeps your existing patients engaged and attracts new followers actively seeking health guidance.",
      },
    ],
    caseStudy: {
      metric: "+95%",
      metricLabel: "new patients per month in 3 months",
      badge: "Chiro · Brisbane · Google Ads + Local SEO + Review Automation",
      title: "Brisbane Chiropractic Clinic — from 22 new patients to 43 per month.",
      body: "A Brisbane chiropractic practice had strong clinical outcomes but was relying solely on referrals and had almost no digital presence. Lumotis built Google Ads targeting back pain and sciatica keywords, optimised their GBP with practitioner photos and weekly posts, and implemented post-appointment review SMS automation. 3-month result: 43 new patients per month (up from 22), 4.9★ from 94 Google reviews, and #1 ranking on Google Maps for 'chiropractor Brisbane CBD'.",
    },
    results: [
      { metric: "+95%", label: "new patients per month", market: "Brisbane · Chiro" },
      { metric: "4.9★", label: "Google review average", market: "94 reviews" },
      { metric: "#1", label: "Google Maps – 'chiropractor Brisbane CBD'", market: "Local SEO" },
      { metric: "−32%", label: "single-visit drop-off rate", market: "Retention automation" },
    ],
    tactics: [
      "Google Ads – back pain keywords",
      "Google Ads – sciatica & injury",
      "Google Local Services Ads",
      "Local SEO – condition searches",
      "Google Business Profile",
      "Post-appointment review SMS",
      "Healthgrades optimisation",
      "Condition landing pages",
      "Practitioner profile pages",
      "Care plan reminder emails",
      "Lapsed patient win-back SMS",
      "Instagram educational content",
      "Facebook health campaigns",
      "YouTube exercise tutorial ads",
      "Symptom-based blog content",
      "Retargeting website visitors",
      "Appointment reminder automation",
      "AI Overview health citations",
      "Schema markup – medical",
      "Online booking CRO",
    ],
    faq: [
      {
        q: "Do you work with both chiropractors and physiotherapists?",
        a: "Yes — and osteopaths, remedial massage therapists, podiatrists and other allied health practitioners too. Each discipline gets a tailored keyword strategy reflecting the specific conditions and search terms relevant to their practice.",
      },
      {
        q: "Can you help a single-practitioner clinic compete with large physio chains?",
        a: "Absolutely. Single-practitioner and small clinics often have a natural advantage in Local SEO — Google Maps tends to favour proximity and reviews over size. We've consistently helped small practices outrank large chains within 3–5 months.",
      },
      {
        q: "How do you handle AHPRA / advertising compliance for allied health?",
        a: "All ad copy and content is reviewed against AHPRA advertising guidelines (Australia) and equivalent regulations in other markets. We avoid prohibited claims, ensure testimonials are compliant and brief you on any content requirements during onboarding.",
      },
      {
        q: "Do you integrate with booking systems like Cliniko or Nookal?",
        a: "Yes. We integrate automation and review triggers with Cliniko, Nookal, Halaxy and most major practice management systems for allied health.",
      },
    ],
    relatedSlugs: ["healthcare-marketing", "dental-marketing", "fitness-gym-marketing"],
  },

  /* ─────────────────────────────── COSMETIC SURGERY & AESTHETICS ─────────────────────────────── */
  {
    slug: "cosmetic-surgery-marketing",
    name: "Cosmetic Surgery & Aesthetics",
    shortName: "Cosmetic",
    emoji: "✨",
    cover: "https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?w=1200&q=85",
    group: "Health & Medical",
    desc: "Attract premium aesthetic clients with compliant digital campaigns.",
    metaTitle: "Cosmetic Surgery Marketing Agency — Premium Patient Acquisition | Lumotis",
    metaDescription: "Lumotis is a specialist cosmetic surgery and medical aesthetics marketing agency. We attract premium patients with Google Ads, SEO, social media and compliant before/after campaigns. Free audit.",
    heroEyebrow: "Digital Marketing for Cosmetic Surgery & Aesthetics",
    heroHeadline: "Cosmetic Marketing That Attracts Premium Patients Ready to Commit",
    heroAccent: "More consultations. Higher case value. Full compliance.",
    heroTagline: "We build high-trust, fully compliant marketing systems for cosmetic surgeons, plastic surgeons and medical aesthetic clinics — Google Ads, SEO, social media and reputation management — attracting the ideal patients for your highest-value procedures.",
    heroStats: [
      { value: "+180%", label: "Avg consultation enquiries" },
      { value: "50+", label: "Aesthetic clinics served" },
      { value: "$4,200", label: "Avg case value of enquiries generated" },
    ],
    insightStats: [
      {
        value: "88%",
        label: "of cosmetic procedure decisions begin with online research",
        desc: "Patients researching rhinoplasty, breast augmentation or injectables spend weeks reading reviews, watching before/after content and comparing surgeons before ever booking a consultation. Your digital presence must be compelling throughout this journey.",
      },
      {
        value: "6-12",
        label: "weeks average consideration period before cosmetic consultation",
        desc: "Long consideration cycles mean remarketing, email nurture and strong content marketing are essential. Patients who don't convert immediately still convert — if you stay visible and trustworthy.",
      },
      {
        value: "3.4×",
        label: "higher conversion from procedure-specific landing pages vs generic sites",
        desc: "Patients searching for a specific procedure want to land on a page dedicated to that procedure — with before/afters, recovery information, pricing context and an easy consultation booking form.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Procedure Enquiries",
        body: "Procedure-specific search campaigns — rhinoplasty, breast augmentation, blepharoplasty, liposuction, injectables — each with dedicated landing pages, before/after galleries and frictionless consultation booking.",
      },
      {
        title: "Procedure-Specific SEO",
        body: "Rank for high-intent procedure searches in your city. Each procedure page is built with clinical depth, FAQ content, recovery guides and structured data — establishing your surgeons as authoritative voices.",
      },
      {
        title: "Compliant Social Media Campaigns",
        body: "Instagram and Facebook campaigns built to platform advertising policies and medical advertising regulations. Testimonial content strategy, patient journey storytelling and educational procedure content.",
      },
      {
        title: "Reputation & Surgeon Authority",
        body: "Google review generation, RealSelf profile optimisation, surgeon bio authority pages and media placement strategy — building the credibility that converts researchers into consultation bookings.",
      },
      {
        title: "Consultation Funnel & Remarketing",
        body: "Multi-step consultation funnels that qualify procedure interest and budget, paired with 90-day remarketing sequences that re-engage patients who visited but didn't book — converting long-cycle researchers.",
      },
      {
        title: "AI Search Visibility & Thought Leadership",
        body: "Surgeon content that earns citations in ChatGPT, Google AI Overviews and medical Q&A platforms when patients research procedures. Establishes your clinic as the most trusted voice in your market.",
      },
    ],
    caseStudy: {
      metric: "+180%",
      metricLabel: "consultation enquiries in 5 months",
      badge: "Cosmetic Surgery · Sydney · Google Ads + SEO + RealSelf",
      title: "Sydney Plastic Surgeon — tripled rhinoplasty consultations with a procedure-first strategy.",
      body: "A Sydney plastic surgeon had a strong reputation but no structured digital marketing — relying on word of mouth for 90% of enquiries. Lumotis built procedure-specific Google Ads for rhinoplasty, breast augmentation and blepharoplasty, created SEO-optimised procedure pages and optimised the surgeon's RealSelf profile. 5-month result: 180% more consultation enquiries, average case value of enquiries at $4,800, and the surgeon's website ranking #1 organically for 'rhinoplasty Sydney'.",
    },
    results: [
      { metric: "+180%", label: "consultation enquiries", market: "Sydney · Plastic Surgery" },
      { metric: "$4,800", label: "avg case value of enquiries", market: "High-value procedures" },
      { metric: "#1", label: "organic rank – 'rhinoplasty Sydney'", market: "SEO" },
      { metric: "4.9★", label: "RealSelf & Google rating", market: "Reputation management" },
    ],
    tactics: [
      "Google Ads – rhinoplasty",
      "Google Ads – breast augmentation",
      "Google Ads – injectables",
      "Procedure landing pages",
      "Before/after gallery SEO",
      "Procedure blog content",
      "FAQ schema markup",
      "RealSelf profile optimisation",
      "Google review generation",
      "Surgeon authority bio pages",
      "Instagram before/after strategy",
      "Facebook patient journey ads",
      "90-day remarketing sequences",
      "Consultation funnel CRO",
      "Email nurture for long-cycle leads",
      "Media placement & PR",
      "YouTube surgeon education",
      "AI Overview citations",
      "Competitive conquesting ads",
      "Local SEO – city + procedure",
    ],
    faq: [
      {
        q: "How do you ensure cosmetic surgery ads comply with platform and regulatory rules?",
        a: "We follow Meta and Google advertising policies for medical content, avoid before/after imagery in paid ads where prohibited, and comply with relevant medical advertising regulations (AHPRA, ASA, etc.) in each market. All ad copy is reviewed for compliance before going live.",
      },
      {
        q: "Do you work with medical aesthetic clinics (injectables, laser) as well as surgeons?",
        a: "Yes. We have specific playbooks for injectable clinics (Botox, filler, PRP), laser and skin clinics, body contouring, and full surgical practices. The strategy is tailored to the specific procedure mix and patient profile.",
      },
      {
        q: "How do you handle the long consideration cycle for cosmetic procedures?",
        a: "Long-cycle remarketing and email nurture are built into every cosmetic campaign from the start. We retarget procedure page visitors for up to 180 days, and deploy educational email sequences that keep your surgeons top-of-mind throughout the research journey.",
      },
      {
        q: "Can you help with international patient acquisition?",
        a: "Yes. We have experience running geo-targeted campaigns to attract medical tourism patients from specific countries — particularly for markets where pricing or procedure availability differs. International campaigns require specific landing page and trust-signal strategy.",
      },
    ],
    relatedSlugs: ["dental-marketing", "healthcare-marketing", "salon-spa-marketing"],
  },

  /* ─────────────────────────────── MENTAL HEALTH & THERAPY ─────────────────────────────── */
  {
    slug: "mental-health-marketing",
    name: "Mental Health & Therapy",
    shortName: "Mental Health",
    emoji: "🧠",
    cover: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=85",
    group: "Health & Medical",
    desc: "Connect people in need with your therapy practice online.",
    metaTitle: "Mental Health Marketing Agency — Grow Your Therapy Practice with Google & Local SEO | Lumotis",
    metaDescription: "Lumotis is a specialist mental health and therapy marketing agency. We help therapists, psychologists and counsellors grow their practice with Google Ads, Local SEO and compliant content marketing. Free audit.",
    heroEyebrow: "Digital Marketing for Mental Health Practitioners",
    heroHeadline: "Mental Health Marketing That Connects the Right Clients to Your Practice",
    heroAccent: "More enquiries. Ethical positioning. A full caseload.",
    heroTagline: "We build ethical, sensitivity-first marketing systems for therapists, psychologists, psychiatrists and counselling practices — helping people who need your support find you, trust you and take the step to reach out.",
    heroStats: [
      { value: "+110%", label: "Avg new client enquiries" },
      { value: "55+", label: "Mental health practices served" },
      { value: "4.7★", label: "Average review rating achieved" },
    ],
    insightStats: [
      {
        value: "65%",
        label: "of therapy clients find their practitioner via Google",
        desc: "People searching for a therapist use Google first — often searching condition-specific terms like 'anxiety therapist near me' or 'CBT psychologist [city]'. Ranking for these searches is the most direct path to new clients.",
      },
      {
        value: "3×",
        label: "more enquiries from practitioners with 50+ Google reviews",
        desc: "Trust is everything in mental health. Practitioners with a substantial, positive review profile receive dramatically more enquiries — people want evidence of a safe, effective experience before making contact.",
      },
      {
        value: "80%",
        label: "of people who seek therapy do so after 3+ weeks of research",
        desc: "Mental health care decisions are not impulsive. Remarketing, educational content and a compassionate email nurture sequence are essential to convert researchers into clients.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Therapy Enquiries",
        body: "Sensitivity-first search campaigns targeting anxiety, depression, trauma, relationship counselling and other condition-specific keywords. Landing pages written with care, empathy and strong CTAs for initial consultation booking.",
      },
      {
        title: "Local SEO for Therapists",
        body: "Rank for '[modality] therapist near me', '[city] psychologist', 'CBT counsellor [suburb]' and every relevant search. Optimised GBP, citation management and practitioner profile pages built for trust and local authority.",
      },
      {
        title: "Reputation & Trust Building",
        body: "Ethical review generation strategy that grows your Google profile with authentic client testimonials. Professional reply management and platform monitoring across Google and Psychology Today.",
      },
      {
        title: "Content Marketing & Specialisation SEO",
        body: "Condition and modality-specific content — anxiety, EMDR, trauma, CBT, couples therapy — that ranks for symptom-based searches and positions your practitioners as the empathetic expert clients are looking for.",
      },
      {
        title: "Psychology Today & Directory Optimisation",
        body: "Optimised profiles across Psychology Today, BetterHelp directories, Zocdoc and other mental health directories — ensuring maximum visibility across every platform where potential clients search.",
      },
      {
        title: "Ethical Nurture Sequences",
        body: "Compassionate email and SMS sequences for people who reach out but aren't ready to book. Mental health decisions take time — we keep your practice front-of-mind with helpful, stigma-reducing content until they're ready.",
      },
    ],
    caseStudy: {
      metric: "+110%",
      metricLabel: "new client enquiries in 4 months",
      badge: "Mental Health · London · Google Ads + Local SEO + Psychology Today",
      title: "London Therapy Group — from part-time to fully booked across 4 practitioners.",
      body: "A London group therapy practice had 4 therapists but inconsistent caseloads — some were fully booked, others had significant gaps. Lumotis built condition-specific Google Ads, optimised each practitioner's GBP and Psychology Today profile, and deployed a compassionate educational email sequence. 4-month result: all 4 practitioners fully booked, enquiries up 110%, and average wait time for new clients went from 0 days to 3 weeks.",
    },
    results: [
      { metric: "+110%", label: "new client enquiries", market: "London · Therapy Group" },
      { metric: "4×", label: "practitioners fully booked", market: "From partial to full" },
      { metric: "3 wks", label: "average client wait time", market: "High demand signal" },
      { metric: "4.8★", label: "Google review average", market: "Trust-building strategy" },
    ],
    tactics: [
      "Google Ads – anxiety & depression",
      "Google Ads – trauma & PTSD",
      "Google Ads – couples therapy",
      "Condition-specific landing pages",
      "Local SEO – modality keywords",
      "Google Business Profile",
      "Psychology Today optimisation",
      "Ethical review generation",
      "Practitioner profile pages",
      "CBT & modality content",
      "Symptom-based blog SEO",
      "Compassionate email nurture",
      "Remarketing – sensitivity-first",
      "Zocdoc profile management",
      "Instagram mental health content",
      "Facebook awareness campaigns",
      "AI Overview citations",
      "FAQ schema markup",
      "Online therapy platform SEO",
      "Crisis resource content strategy",
    ],
    faq: [
      {
        q: "How do you market mental health services sensitively and ethically?",
        a: "All our mental health campaigns are written with empathy-first language, avoid stigmatising framing, and comply with platform advertising policies for health and mental health services. We never use urgency tactics or fear-based messaging in mental health marketing.",
      },
      {
        q: "Do you work with individual therapists or group practices?",
        a: "Both. Solo practitioners benefit from personal brand and local SEO positioning. Group practices need a coordinated strategy that ensures all practitioners are well-represented and enquiries are routed appropriately.",
      },
      {
        q: "Are Google Ads appropriate for mental health — won't they seem too commercial?",
        a: "When written well, Google Ads for mental health are not just appropriate — they actively help people who are suffering find the support they need. The key is empathetic copy, condition-specific relevance and landing pages that feel safe and welcoming.",
      },
      {
        q: "Do you work with telehealth and online therapy practices?",
        a: "Yes. Telehealth practices can reach clients across a much broader geographic area. We build geo-targeting strategies appropriate for online therapy, including national or multi-state campaigns.",
      },
    ],
    relatedSlugs: ["healthcare-marketing", "coaching-consulting-marketing", "chiropractic-marketing"],
  },

  /* ─────────────────────────────── SALON & SPA ─────────────────────────────── */
  {
    slug: "salon-spa-marketing",
    name: "Salon & Spa Marketing",
    shortName: "Salon & Spa",
    emoji: "✂️",
    cover: "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=1200&q=85",
    group: "Beauty & Wellness",
    desc: "Keep your chairs full with bookings, reviews and retention.",
    metaTitle: "Salon & Spa Marketing Agency — Fill Bookings, Grow Reviews & Build Client Loyalty | Lumotis",
    metaDescription: "Lumotis is the #1 marketing agency for salons and spas. We fill your booking calendar with Google Ads, Instagram, Local SEO and review automation. Free marketing audit.",
    heroEyebrow: "Digital Marketing for Salons & Spas",
    heroHeadline: "Salon & Spa Marketing That Keeps Your Chair Full Every Day",
    heroAccent: "More bookings. Better reviews. Loyal repeat clients.",
    heroTagline: "We run the complete marketing engine for salons, spas, barbers and beauty clinics — Local SEO, Instagram, Google Ads, review automation and client retention sequences — so every stylist is booked and your chair never sits empty.",
    heroStats: [
      { value: "+60%", label: "Avg booking increase" },
      { value: "200+", label: "Salons & spas served" },
      { value: "4.9★", label: "Average client review rating" },
    ],
    insightStats: [
      {
        value: "86%",
        label: "of people read reviews before booking a salon",
        desc: "Your Google and Yelp rating is the single biggest conversion factor in beauty services. One extra star can double your booking rate. We build review velocity systematically.",
      },
      {
        value: "3×",
        label: "more bookings from Instagram vs any other social platform",
        desc: "Beauty is visual. Instagram Reels and Stories drive direct appointment bookings when paired with a clear CTA and booking link. Most salons post inconsistently — we fix that.",
      },
      {
        value: "68%",
        label: "of salon revenue comes from repeat clients",
        desc: "Keeping a client costs 5× less than acquiring one. A well-built retention sequence — birthday offers, rebooking reminders, loyalty rewards — is the highest-ROI investment a salon can make.",
      },
    ],
    pillars: [
      {
        title: "Local SEO & Google Business Profile",
        body: "Rank #1 for '[service] near me', '[city] hair salon', '[city] day spa' and every local search your ideal clients make. We optimise your GBP weekly, build local citations and keep photos and posts fresh.",
      },
      {
        title: "Instagram & TikTok Content",
        body: "Weekly Reels, Stories and TikToks that showcase your stylists' work, build trust and drive direct booking clicks. Full content calendar, filming briefs and hashtag strategy included.",
      },
      {
        title: "Google Ads for Bookings",
        body: "High-intent search campaigns targeting new clients searching for your services right now. Click-to-book extensions, call tracking and bidding optimised for cost-per-booked-appointment.",
      },
      {
        title: "Review Generation & Reputation",
        body: "Automated SMS review requests sent after every appointment. Replies to 1–3★ reviews within 24h. Monthly reputation score reporting. Most clients reach 4.8–4.9★ within 90 days.",
      },
      {
        title: "Email & SMS Retention Sequences",
        body: "Rebooking reminders, birthday offers, seasonal promotions and loyalty reward messages sent at exactly the right time. Keeps your existing clients coming back — and referring friends.",
      },
      {
        title: "Website & Booking Optimisation",
        body: "We audit your booking flow for friction — slow load times, unclear CTAs, missing trust signals. Small CRO fixes on booking pages typically lift conversion 20–40% without any extra ad spend.",
      },
    ],
    caseStudy: {
      metric: "+60%",
      metricLabel: "bookings in the first 90 days",
      badge: "Salon · London · Local SEO + Instagram + Review Automation",
      title: "London Hair Salon — from half-empty books to a 3-week waiting list.",
      body: "A Shoreditch hair salon had a beautiful Instagram feed but was invisible on Google and averaging 3.9★ reviews. Lumotis rebuilt their GBP, launched a Google Ads campaign for high-value colouring services, and deployed an automated post-visit SMS review sequence. 90-day result: 4.8★ average, +60% appointment bookings, and a 3-week waiting list for colour appointments.",
    },
    results: [
      { metric: "+60%", label: "bookings in 90 days", market: "London · Hair Salon" },
      { metric: "4.8★", label: "Google review average", market: "Up from 3.9★" },
      { metric: "#1", label: "Google Maps — 'Shoreditch hair salon'", market: "Local SEO" },
      { metric: "3 wks", label: "waiting list for colour services", market: "Peak demand" },
    ],
    tactics: [
      "Google Business Profile optimisation",
      "Local SEO – service keywords",
      "'Near me' search optimisation",
      "Google Ads – high-value services",
      "Click-to-call campaigns",
      "Instagram Reels production",
      "TikTok before/after content",
      "Stories with booking CTAs",
      "Automated SMS review requests",
      "Reputation monitoring",
      "Review response management",
      "Birthday offer automation",
      "Rebooking reminder SMS",
      "Loyalty program emails",
      "Seasonal promotions",
      "Booking page CRO",
      "Local citation building",
      "Facebook retargeting",
      "Influencer gifting",
      "AI Overview citations",
    ],
    faq: [
      {
        q: "How quickly will we see more bookings?",
        a: "Google Ads and Local SEO updates can start driving new bookings within 1–2 weeks. Review automation typically shows a rating improvement within 30 days. The full system — SEO + Ads + Social + Retention — reaches peak performance at the 60–90 day mark.",
      },
      {
        q: "We don't have a big social media following — can you still help?",
        a: "Absolutely. Most of our salon clients start with small or inconsistent accounts. We build your presence from the ground up with a consistent content calendar. Organic growth compounds; Google Ads drives immediate bookings while the organic base builds.",
      },
      {
        q: "Do you work with individual stylists or only salons?",
        a: "Both. We have packages for solo stylists building a personal brand, small 2–5 chair salons, and multi-location salon groups. Each gets a strategy calibrated to their size and goals.",
      },
      {
        q: "Can you help with our online booking system?",
        a: "We integrate with Fresha, Vagaro, Booksy, StyleSeat, Square and most major booking platforms. We optimise the booking flow for conversion and ensure your booking link is prominent across every marketing channel we run.",
      },
    ],
    relatedSlugs: ["fitness-gym-marketing", "cosmetic-surgery-marketing", "veterinary-marketing"],
  },

  /* ─────────────────────────────── FITNESS & GYM ─────────────────────────────── */
  {
    slug: "fitness-gym-marketing",
    name: "Fitness & Gym Marketing",
    shortName: "Fitness",
    emoji: "💪",
    cover: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=85",
    group: "Beauty & Wellness",
    desc: "Grow memberships and class bookings with targeted campaigns.",
    metaTitle: "Fitness & Gym Marketing Agency — Grow Members with Google Ads & Social | Lumotis",
    metaDescription: "Lumotis is a specialist fitness and gym marketing agency. We grow memberships and class bookings with Google Ads, Facebook, Instagram, Local SEO and member retention campaigns. Free audit.",
    heroEyebrow: "Digital Marketing for Gyms & Fitness Studios",
    heroHeadline: "Gym Marketing That Fills Your Floor and Slashes Membership Churn",
    heroAccent: "More members. Lower churn. A community that sells itself.",
    heroTagline: "We build the complete growth engine for gyms, fitness studios, CrossFit boxes and boutique fitness brands — paid acquisition, Local SEO, social content and member retention automation — driving consistent membership growth all year round.",
    heroStats: [
      { value: "+75%", label: "Avg membership growth" },
      { value: "100+", label: "Gyms & studios served" },
      { value: "−28%", label: "Average membership churn reduction" },
    ],
    insightStats: [
      {
        value: "78%",
        label: "of new gym members make the decision in January or after a life event",
        desc: "Seasonal campaign planning around New Year, summer body season and back-to-school is essential for gyms. But year-round digital presence ensures you capture life-event-driven joiners every month.",
      },
      {
        value: "4×",
        label: "higher trial conversion with free-trial funnel vs direct membership offer",
        desc: "Free week, free class or discounted first month funnels consistently outperform direct membership offers. Lowering the barrier to entry dramatically increases top-of-funnel volume.",
      },
      {
        value: "65%",
        label: "of members who cancel do so in the first 90 days",
        desc: "Early retention — onboarding sequences, milestone emails, community posts and personal check-ins — reduces early-stage churn significantly and increases lifetime member value.",
      },
    ],
    pillars: [
      {
        title: "Google Ads & Local SEO",
        body: "Search campaigns targeting 'gym near me', 'CrossFit [suburb]', 'pilates studio [city]' and every fitness-specific keyword in your catchment. Local SEO ensures you dominate Google Maps for every relevant search.",
      },
      {
        title: "Facebook & Instagram Lead Generation",
        body: "Free trial and intro offer campaigns on Facebook and Instagram with compelling creative, demographic targeting and lead funnels optimised for lowest cost-per-member-acquisition.",
      },
      {
        title: "Member Retention & Onboarding Automation",
        body: "Welcome sequences, 30/60/90 day milestone emails, attendance re-engagement SMS and win-back campaigns for lapsed members. Keeping one member costs a fraction of acquiring a new one.",
      },
      {
        title: "Community & Social Content",
        body: "Weekly social content — member spotlights, coach profiles, transformation stories and educational fitness content — that builds community, drives word-of-mouth referrals and keeps members motivated.",
      },
      {
        title: "Google Business Profile & Reviews",
        body: "Optimised GBP with fresh class schedule photos, post-class review requests and professional reply management. A strong review profile is the #1 trust signal for gym prospects.",
      },
      {
        title: "Referral & Loyalty Campaigns",
        body: "Structured member referral programs with automated incentive delivery, seasonal challenge campaigns and loyalty reward emails — turning your existing member base into your most effective acquisition channel.",
      },
    ],
    caseStudy: {
      metric: "+75%",
      metricLabel: "active memberships in 6 months",
      badge: "Gym · Melbourne · Facebook + Google Ads + Retention",
      title: "Melbourne Boutique Gym — from 180 to 315 active members in 6 months.",
      body: "A Melbourne boutique gym was at 180 active members with high January-spike-then-churn pattern. Lumotis built a Facebook free-trial funnel, Google Ads for 'gym near me' searches, a structured onboarding email sequence and a member referral programme. 6-month result: 315 active members, churn reduced by 28%, and the referral programme now generates 22% of all new members.",
    },
    results: [
      { metric: "+75%", label: "active memberships", market: "Melbourne · Boutique Gym" },
      { metric: "−28%", label: "membership churn rate", market: "Onboarding automation" },
      { metric: "22%", label: "new members from referrals", market: "Referral programme" },
      { metric: "#1", label: "Google Maps – 'gym Melbourne CBD'", market: "Local SEO" },
    ],
    tactics: [
      "Google Ads – 'gym near me'",
      "Google Ads – class-specific keywords",
      "Local SEO – fitness studio",
      "Google Business Profile",
      "Facebook free-trial campaigns",
      "Instagram transformation content",
      "Lead generation funnels",
      "Free trial landing pages",
      "Member onboarding email sequence",
      "30/60/90 day milestone emails",
      "Attendance re-engagement SMS",
      "Lapsed member win-back",
      "Member referral programme",
      "Google review generation",
      "Community social content",
      "Coach profile content",
      "Seasonal challenge campaigns",
      "Loyalty reward emails",
      "YouTube workout ads",
      "Retargeting website visitors",
    ],
    faq: [
      {
        q: "Do you work with large commercial gyms and small boutique studios?",
        a: "Both. Large commercial gyms benefit from high-volume acquisition campaigns and territory-based local SEO. Boutique studios — pilates, yoga, CrossFit, HIIT — benefit from community content, referral programmes and premium positioning strategies.",
      },
      {
        q: "How do you handle the January spike — and keep members year-round?",
        a: "We plan campaigns around January and other high-intent periods (summer, back-to-school), but the real value is the retention system. Onboarding automation, milestone emails and community content significantly reduce post-January churn.",
      },
      {
        q: "Can you promote specific class types or instructor-led programmes?",
        a: "Yes. We run class-specific campaigns for high-margin offerings — yoga, HIIT, boxing, reformer pilates — with dedicated landing pages and social content featuring the specific instructors.",
      },
      {
        q: "Do you integrate with gym management software?",
        a: "We integrate with Mindbody, Glofox, TeamUp, PushPress and most major gym management systems for membership tracking, class booking conversion and retention automation triggers.",
      },
    ],
    relatedSlugs: ["personal-trainer-marketing", "salon-spa-marketing", "chiropractic-marketing"],
  },

  /* ─────────────────────────────── VETERINARY & PET SERVICES ─────────────────────────────── */
  {
    slug: "veterinary-marketing",
    name: "Veterinary & Pet Services",
    shortName: "Vet & Pet",
    emoji: "🐾",
    cover: "https://images.unsplash.com/photo-1512568400610-62da28bc8a13?w=1200&q=85",
    group: "Beauty & Wellness",
    desc: "Attract pet owners and fill your appointment book year-round.",
    metaTitle: "Veterinary Marketing Agency — Attract Pet Owners with Google & Local SEO | Lumotis",
    metaDescription: "Lumotis is a specialist veterinary and pet services marketing agency. We fill your appointment book with Google Ads, Local SEO, review automation and client retention campaigns. Free audit.",
    heroEyebrow: "Digital Marketing for Vets & Pet Services",
    heroHeadline: "Vet Marketing That Attracts New Pet Owners and Builds Client Loyalty",
    heroAccent: "More appointments. Stronger reviews. Pets and owners who stay for life.",
    heroTagline: "We build the complete marketing engine for veterinary clinics, animal hospitals, pet grooming and boarding services — Google Ads, Local SEO, review management and client retention — ensuring your clinic is the obvious choice for pet owners in your area.",
    heroStats: [
      { value: "+80%", label: "Avg new client enquiries" },
      { value: "65+", label: "Vet clinics & pet businesses served" },
      { value: "4.8★", label: "Average review rating achieved" },
    ],
    insightStats: [
      {
        value: "73%",
        label: "of pet owners choose a vet based on Google reviews and proximity",
        desc: "When a pet owner moves to a new area or switches vets, Google Maps is their first stop. A strong GBP with recent reviews and accurate information is the most important acquisition asset for any vet practice.",
      },
      {
        value: "85%",
        label: "of pet owners consider their pets family members",
        desc: "Pet owners make emotional, high-trust decisions about veterinary care. Marketing that leads with empathy, expertise and trust — not price — consistently outperforms discount-led approaches.",
      },
      {
        value: "2.8×",
        label: "higher lifetime client value from clients acquired via Google Ads vs directories",
        desc: "Intent-driven clients who search for a vet and choose you actively tend to have higher visit frequency, higher average spend and better retention than passive directory leads.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for New Pet Clients",
        body: "Search campaigns targeting 'vet near me', 'emergency vet [city]', 'cat vet [suburb]' and service-specific keywords. Landing pages with trust signals, team profiles and simple online booking for new patient appointments.",
      },
      {
        title: "Local SEO & Google Maps",
        body: "Rank #1 for every 'veterinarian near me' and service-specific search in your area. We optimise your GBP with clinical photography, weekly posts and service category accuracy.",
      },
      {
        title: "Review Generation & Reputation",
        body: "Post-appointment automated review requests via SMS. Professional reply management across Google and Facebook. Most clinics move from 4.2★ to 4.8★ within 90 days of systematic review operations.",
      },
      {
        title: "Client Retention & Recall Automation",
        body: "Annual vaccination reminders, dental health campaigns, heartworm prevention alerts and post-surgery follow-up sequences that keep pets on schedule — and clients loyal to your clinic.",
      },
      {
        title: "Social Media & Pet Content",
        body: "Heartwarming patient stories, pet care tip videos, team introductions and seasonal health alerts for Instagram and Facebook — building an emotional connection with pet owners in your community.",
      },
      {
        title: "Pet Services Marketing (Grooming, Boarding, Training)",
        body: "Tailored campaigns for grooming salons, dog daycare, boarding kennels and pet training businesses — with seasonal campaign planning for peak demand periods like school holidays.",
      },
    ],
    caseStudy: {
      metric: "+80%",
      metricLabel: "new client registrations per month",
      badge: "Veterinary · Auckland · Google Ads + Local SEO + Review Automation",
      title: "Auckland Vet Clinic — from 25 new clients/month to 45 in 3 months.",
      body: "An Auckland veterinary clinic was struggling to grow beyond a plateau of 25 new clients per month despite a strong clinical team. Lumotis built Google Ads targeting new-to-area pet owners, optimised their GBP with professional clinic photography and deployed automated post-visit review requests. 3-month result: 45 new clients per month, 4.9★ from 112 Google reviews, and the clinic's #1 Google Maps ranking for 'vet Auckland CBD'.",
    },
    results: [
      { metric: "+80%", label: "new client registrations", market: "Auckland · Vet" },
      { metric: "4.9★", label: "Google review average", market: "112 reviews" },
      { metric: "#1", label: "Google Maps – 'vet Auckland CBD'", market: "Local SEO" },
      { metric: "+35%", label: "annual wellness plan uptake", market: "Retention campaigns" },
    ],
    tactics: [
      "Google Ads – 'vet near me'",
      "Google Ads – emergency vet",
      "Google Ads – specialist services",
      "Local SEO – vet keywords",
      "Google Business Profile",
      "Post-appointment review SMS",
      "Vaccination recall automation",
      "Annual wellness reminders",
      "Dental health campaigns",
      "Heartworm prevention alerts",
      "Post-surgery follow-up emails",
      "Instagram pet patient stories",
      "Facebook community content",
      "Pet care tip videos",
      "Grooming seasonal campaigns",
      "Boarding school holiday ads",
      "Facebook retargeting",
      "Local citation building",
      "New-mover targeting campaigns",
      "AI Overview vet citations",
    ],
    faq: [
      {
        q: "Do you work with specialist veterinary practices as well as general clinics?",
        a: "Yes — including oncology, orthopaedics, dermatology and emergency/critical care vet practices. Specialist practices require different keyword strategies and content authority positioning.",
      },
      {
        q: "Can you help with pet grooming, boarding and training businesses too?",
        a: "Absolutely. We have specific playbooks for each pet service category with seasonal campaign planning, particularly for boarding services ahead of school holidays and peak travel periods.",
      },
      {
        q: "How do you target new-to-area pet owners specifically?",
        a: "We use new-mover targeting through Facebook and Google audiences, plus local SEO that captures 'vet near me' searches from people who've just moved to your area — one of the highest-value acquisition moments for vet clinics.",
      },
      {
        q: "Do you integrate with veterinary practice management software?",
        a: "We integrate with IDEXX Animana, RxWorks, AVImark and other major VMS platforms for recall automation and appointment-based review request triggers.",
      },
    ],
    relatedSlugs: ["salon-spa-marketing", "fitness-gym-marketing", "healthcare-marketing"],
  },

  /* ─────────────────────────────── RESTAURANTS & FOOD ─────────────────────────────── */
  {
    slug: "restaurant-marketing",
    name: "Restaurants & Food",
    shortName: "Restaurant",
    emoji: "🍽️",
    cover: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=85",
    group: "Food & Hospitality",
    desc: "Fill every cover every night with Google, social and reviews.",
    metaTitle: "Restaurant Marketing Agency — Fill Tables, Grow Reviews & Drive Online Orders | Lumotis",
    metaDescription: "Lumotis is a specialist restaurant and food business marketing agency. We fill your tables with Local SEO, Google Ads, Instagram, review management and online ordering campaigns. Free audit.",
    heroEyebrow: "Digital Marketing for Restaurants",
    heroHeadline: "Restaurant Marketing That Fills Every Cover, Every Night",
    heroAccent: "More covers. More orders. A reputation that sells itself.",
    heroTagline: "We run the complete digital marketing engine for restaurants, cafes, bars, cloud kitchens and food brands — Local SEO, Google Ads, Instagram, review management and online ordering campaigns — so every service is full and your brand is the obvious choice.",
    heroStats: [
      { value: "+45%", label: "Avg cover increase" },
      { value: "150+", label: "Food businesses served" },
      { value: "4.8★", label: "Average review rating achieved" },
    ],
    insightStats: [
      {
        value: "90%",
        label: "of diners research a restaurant online before visiting",
        desc: "Your Google rating, photos, menu and response to reviews are all judged before anyone steps through the door. First impressions are now digital.",
      },
      {
        value: "5×",
        label: "more bookings from Google vs social media for restaurants",
        desc: "Google Search and Google Maps drive the majority of new diners — not Instagram. Local SEO and Google Ads are the highest-ROI channels for most food businesses.",
      },
      {
        value: "30%",
        label: "of restaurant revenue now comes from online delivery",
        desc: "Delivery platform visibility, Google ordering integration and social-driven order campaigns are essential revenue streams — not optional add-ons.",
      },
    ],
    pillars: [
      {
        title: "Local SEO & Google Maps",
        body: "Rank #1 for '[cuisine] restaurant near me', '[city] best [dish]' and every hungry search in your area. We optimise your GBP weekly with fresh photos, posts and menu updates — and build citations across Yelp, TripAdvisor and local directories.",
      },
      {
        title: "Google Ads for Reservations & Orders",
        body: "Search campaigns targeting diners actively looking for your cuisine type, event packages, or delivery. Click-to-call and reservation extensions. Bidding optimised for cost-per-seated-cover.",
      },
      {
        title: "Instagram & TikTok Food Content",
        body: "Weekly food photography briefs, Reels of signature dishes, behind-the-scenes kitchen content and Stories with reservation CTAs. Content that makes people hungry — and act on it.",
      },
      {
        title: "Review Management & Reputation",
        body: "Post-visit automated review requests via SMS. Professional replies to every review within 24h. TripAdvisor, Google and Yelp monitoring. Most restaurants reach 4.7–4.9★ within 90 days of systematic operations.",
      },
      {
        title: "Online Ordering & Delivery Campaigns",
        body: "Delivery platform profile optimisation (Deliveroo, Uber Eats, Just Eat/DoorDash), Google ordering integration and paid campaigns that drive direct order volume — reducing commission dependency.",
      },
      {
        title: "Email & SMS Loyalty Marketing",
        body: "Reservation follow-ups, birthday dining offers, seasonal menu launches and loyalty reward campaigns that bring existing customers back — and get them to bring friends.",
      },
    ],
    caseStudy: {
      metric: "+45%",
      metricLabel: "covers per month in 60 days",
      badge: "Restaurant · Sydney · Local SEO + Instagram + Google Ads",
      title: "Sydney Modern Asian Restaurant — from 60% occupancy to fully booked weekends.",
      body: "A Sydney contemporary Asian restaurant had a stunning dining room but was consistently at 60% weekend occupancy and barely breaking even. Lumotis rebuilt their Google Business Profile with professional food photography, launched weekend Google Ads campaigns and deployed a weekly Instagram Reels strategy. 60 days later: weekends fully booked 3 weeks in advance, Google Maps ranking #1 for 'modern Asian Sydney', and a 4.8★ rating from 310 reviews.",
    },
    results: [
      { metric: "+45%", label: "covers per month in 60 days", market: "Sydney · Restaurant" },
      { metric: "#1", label: "Google Maps – 'modern Asian Sydney'", market: "Local SEO" },
      { metric: "4.8★", label: "Google review average", market: "310 reviews" },
      { metric: "3 wks", label: "weekend advance booking waitlist", market: "Peak occupancy" },
    ],
    tactics: [
      "Google Business Profile",
      "Google Maps – cuisine searches",
      "'Near me' restaurant SEO",
      "Google Ads – reservations",
      "Click-to-call campaigns",
      "Instagram food Reels",
      "TikTok kitchen content",
      "Stories with booking CTAs",
      "Food photography briefs",
      "TripAdvisor optimisation",
      "Yelp profile management",
      "Post-visit SMS review requests",
      "Review reply management",
      "Deliveroo/Uber Eats optimisation",
      "Google ordering integration",
      "Birthday dining SMS",
      "Loyalty email sequences",
      "Event & private dining campaigns",
      "Seasonal menu launches",
      "Facebook events promotion",
    ],
    faq: [
      {
        q: "How quickly will we see more covers from digital marketing?",
        a: "Google Ads and Local SEO updates typically generate measurable increases in reservations and walk-ins within 2–4 weeks of launch. Instagram compounds over 60–90 days as the content library builds. Most restaurant clients see meaningful cover increases within the first 30 days.",
      },
      {
        q: "Do you work with delivery-only (cloud kitchen) businesses?",
        a: "Yes. Cloud kitchens require a different approach — delivery platform optimisation, targeted Google Ads for delivery keywords, and social content that drives order volume. We have specific playbooks for delivery-first food businesses.",
      },
      {
        q: "We already have a social media manager — can you just handle the ads and SEO?",
        a: "Absolutely. We offer modular services — you can engage us for just Local SEO + Google Ads, or just paid social, or the full stack. We'll coordinate with your existing team and fill the gaps in your current strategy.",
      },
      {
        q: "Can you help with our launch marketing for a new restaurant?",
        a: "Yes — launch marketing is a speciality. We build pre-launch buzz campaigns, soft-opening review events, launch-week social content calendars and Google Ads that drive traffic from day one. Most new restaurant clients are fully booked within the first few weeks of opening.",
      },
    ],
    relatedSlugs: ["travel-hospitality-marketing", "salon-spa-marketing", "wedding-events-marketing"],
  },

  /* ─────────────────────────────── TRAVEL & HOSPITALITY ─────────────────────────────── */
  {
    slug: "travel-hospitality-marketing",
    name: "Travel & Hospitality",
    shortName: "Travel",
    emoji: "✈️",
    cover: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=85",
    group: "Food & Hospitality",
    desc: "Fill rooms, tours and experiences with high-intent travellers.",
    metaTitle: "Travel & Hospitality Marketing Agency — Drive Direct Bookings & Beat OTAs | Lumotis",
    metaDescription: "Lumotis is a specialist travel and hospitality marketing agency. We drive direct bookings for hotels, tour operators and travel brands with Google Ads, Meta, SEO and email. Free audit.",
    heroEyebrow: "Digital Marketing for Travel & Hospitality",
    heroHeadline: "Travel Marketing That Drives Direct Bookings and Reduces OTA Dependency",
    heroAccent: "More direct bookings. Lower commission costs. Guests who return.",
    heroTagline: "We build the full digital marketing engine for hotels, resorts, tour operators, travel agencies and experience businesses — Google Ads, Meta, SEO and email — driving direct bookings that cost you a fraction of OTA commissions.",
    heroStats: [
      { value: "+55%", label: "Avg direct booking increase" },
      { value: "40+", label: "Travel & hospitality brands served" },
      { value: "−30%", label: "OTA commission dependency reduced" },
    ],
    insightStats: [
      {
        value: "83%",
        label: "of travellers research online before booking",
        desc: "Google, TripAdvisor, Instagram and YouTube are part of the travel research journey. Brands visible across all stages of this journey — inspiration, research, booking — consistently win more direct customers.",
      },
      {
        value: "25%",
        label: "average OTA commission rate eating into hotel margins",
        desc: "Every direct booking saves you 15–25% in commission. A $2M annual revenue hotel saving 10 points of OTA dependency frees $200,000 in margin. Direct channel investment pays for itself rapidly.",
      },
      {
        value: "3.5×",
        label: "higher guest lifetime value from direct vs OTA bookings",
        desc: "Direct guests can be remarketed, emailed and nurtured into returning customers. OTA guests are anonymous — you have no data and no ability to build loyalty.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Direct Bookings",
        body: "Search campaigns targeting destination, property and experience keywords — with booking-engine-integrated landing pages, dynamic date-range ads and bidding strategies calibrated to revenue-per-available-room.",
      },
      {
        title: "Meta & Instagram Travel Campaigns",
        body: "Inspirational destination content for prospecting, retargeting campaigns for website visitors and lookalike audiences built on past direct bookers. Dynamic travel ads with real-time availability and pricing.",
      },
      {
        title: "Travel SEO & Destination Content",
        body: "Destination guide content, experience pages, FAQ SEO and structured data that captures high-intent traveller searches — establishing your brand as an authoritative voice in your destination or niche.",
      },
      {
        title: "OTA Competitor Campaigns",
        body: "Branded search campaigns that intercept travellers who find you on Booking.com or Expedia and redirect them to your direct booking channel — at a fraction of the OTA commission cost.",
      },
      {
        title: "Email Marketing & Guest Retention",
        body: "Post-stay review requests, anniversary return offers, seasonal promotion emails and loyalty programme campaigns — turning one-time guests into repeat direct bookers.",
      },
      {
        title: "TripAdvisor & OTA Profile Optimisation",
        body: "Optimised TripAdvisor, Booking.com and Google Hotel profiles with strategic photography, response management and review velocity — because OTA presence still drives discovery even as we shift bookings direct.",
      },
    ],
    caseStudy: {
      metric: "+55%",
      metricLabel: "direct bookings in 5 months",
      badge: "Hospitality · Bali · Google Ads + Meta + Email",
      title: "Bali Boutique Resort — reduced OTA dependency from 78% to 52% of bookings.",
      body: "A Bali boutique resort was paying 22% average commission on 78% of bookings through OTA platforms. Lumotis built a Google Ads campaign intercepting branded OTA searchers, launched Instagram travel campaigns targeting their ideal demographic and deployed a post-stay email sequence with return offers. 5-month result: direct bookings up 55%, OTA share down to 52%, and $140,000 in annual commission savings.",
    },
    results: [
      { metric: "+55%", label: "direct bookings", market: "Bali · Boutique Resort" },
      { metric: "−26%", label: "OTA booking share", market: "From 78% to 52%" },
      { metric: "$140k", label: "annual commission savings", market: "22% OTA rate" },
      { metric: "4.9★", label: "TripAdvisor rating", market: "Review management" },
    ],
    tactics: [
      "Google Ads – destination keywords",
      "Branded OTA intercept campaigns",
      "Google Hotel Ads",
      "Meta travel prospecting",
      "Instagram destination Reels",
      "Dynamic travel retargeting",
      "Destination guide SEO",
      "Experience page content",
      "TripAdvisor profile optimisation",
      "Post-stay review SMS",
      "Guest email sequences",
      "Return visit offer campaigns",
      "Seasonal promotion emails",
      "Loyalty programme campaigns",
      "YouTube destination videos",
      "Pinterest travel content",
      "Booking.com strategy",
      "AI Overview travel citations",
      "Lookalike audience – past bookers",
      "Corporate travel B2B campaigns",
    ],
    faq: [
      {
        q: "Do you work with small boutique properties as well as large hotel groups?",
        a: "Yes. Boutique hotels and small resorts often see the highest ROI from direct booking campaigns because their OTA commission savings are proportionally large and they can build a distinct brand story more easily.",
      },
      {
        q: "Can you help tour operators and experience businesses, not just hotels?",
        a: "Absolutely. Tour operators, activity providers, safari camps, cruise lines and experience businesses all benefit from our direct booking strategy. We tailor campaigns to the specific booking journey for experiences vs accommodation.",
      },
      {
        q: "How do you integrate with booking engines?",
        a: "We integrate with most major booking engines — ResNexus, Cloudbeds, Opera, Little Hotelier and others — to track direct booking conversions accurately and optimise campaigns by revenue, not just clicks.",
      },
      {
        q: "How long does it take to meaningfully shift bookings from OTAs to direct?",
        a: "Most clients see meaningful shifts in OTA vs direct booking mix within 3–5 months. Google Ads intercept campaigns work quickly — often within weeks. SEO and brand-building take longer but create a sustainable, compounding advantage.",
      },
    ],
    relatedSlugs: ["restaurant-marketing", "wedding-events-marketing", "photography-marketing"],
  },

  /* ─────────────────────────────── LAW FIRMS & LEGAL ─────────────────────────────── */
  {
    slug: "law-firm-marketing",
    name: "Law Firms & Legal",
    shortName: "Legal",
    emoji: "⚖️",
    cover: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&q=85",
    group: "Legal & Finance",
    desc: "Generate qualified legal enquiries with Google and authority content.",
    metaTitle: "Law Firm Marketing Agency — Qualified Legal Leads with Google Ads & SEO | Lumotis",
    metaDescription: "Lumotis is a specialist law firm marketing agency. We generate qualified legal enquiries with Google Ads, Local SEO, content marketing and reputation management. Free audit.",
    heroEyebrow: "Digital Marketing for Law Firms",
    heroHeadline: "Legal Marketing That Brings Qualified Cases to Your Firm Every Month",
    heroAccent: "More qualified enquiries. Higher case value. Dominant local authority.",
    heroTagline: "We build high-performance, compliance-aware marketing systems for law firms, solicitors and barristers — Google Ads, Local SEO, content authority and reputation management — generating a consistent flow of qualified legal enquiries.",
    heroStats: [
      { value: "+160%", label: "Avg qualified enquiry increase" },
      { value: "45+", label: "Law firms served" },
      { value: "$8,500", label: "Avg case value of enquiries generated" },
    ],
    insightStats: [
      {
        value: "96%",
        label: "of people searching for a lawyer use Google as the first step",
        desc: "Legal services are high-stakes decisions made after significant online research. Being visible at the top of Google — in ads and organically — is non-negotiable for modern law firms.",
      },
      {
        value: "4.2×",
        label: "higher conversion rate from practice-area-specific landing pages",
        desc: "People searching for a personal injury lawyer don't want a generic 'we do everything' page. Dedicated practice area landing pages dramatically increase conversion rates by matching the specific legal need.",
      },
      {
        value: "72%",
        label: "of legal clients choose the firm with the most reviews in their practice area",
        desc: "Google reviews are the primary trust signal in legal services. Firms with 100+ reviews and 4.7★+ consistently win enquiries over equally qualified competitors with few or poor reviews.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Legal Leads",
        body: "Practice-area-specific search campaigns — personal injury, family law, criminal defence, immigration, employment, conveyancing — each with dedicated landing pages, click-to-call and bidding calibrated to case value.",
      },
      {
        title: "Legal SEO & Content Authority",
        body: "Practice area pages, condition-specific legal guides, FAQ content and local authority pages that rank for high-intent legal searches. Legal content written by subject-matter-aware copywriters who understand the nuance of legal marketing.",
      },
      {
        title: "Google Business Profile & Local SEO",
        body: "Rank #1 in Google Maps for '[practice area] solicitor [city]', '[law firm type] near me' and every jurisdiction-specific legal search. Citation building and NAP management across legal directories.",
      },
      {
        title: "Reputation Management",
        body: "Systematic post-matter review generation across Google and Avvo/Martindale. Professional reply management. Reputation monitoring. Most firms reach 4.7–4.9★ within 90 days of systematic review operations.",
      },
      {
        title: "Legal Directory Optimisation",
        body: "Optimised profiles on Avvo, FindLaw, Martindale-Hubbell, Yelp and jurisdiction-specific directories — ensuring maximum visibility across every platform where prospective clients search for legal representation.",
      },
      {
        title: "AI Search & Thought Leadership",
        body: "When people ask ChatGPT or Google AI Overviews legal questions, your firm and your attorneys should be the cited authority. We build the content infrastructure that earns those citations.",
      },
    ],
    caseStudy: {
      metric: "+160%",
      metricLabel: "qualified enquiries per month",
      badge: "Legal · London · Google Ads + SEO + Reputation",
      title: "London Personal Injury Firm — from 12 qualified enquiries to 31 per month.",
      body: "A London personal injury firm was spending heavily on comparison aggregators but receiving low-quality leads. Lumotis built practice-specific Google Ads for personal injury and medical negligence, created practice area landing pages and SEO content, and deployed a systematic Google review programme. 5-month result: 31 qualified enquiries per month (up from 12), average case value of enquiries at £11,000, and #1 Google Maps ranking for 'personal injury solicitor London'.",
    },
    results: [
      { metric: "+160%", label: "qualified enquiries per month", market: "London · Personal Injury" },
      { metric: "£11,000", label: "avg case value of enquiries", market: "Google Ads + SEO" },
      { metric: "#1", label: "Google Maps – practice area + city", market: "Local SEO" },
      { metric: "4.8★", label: "Google review average", market: "135 reviews" },
    ],
    tactics: [
      "Google Ads – personal injury",
      "Google Ads – family law",
      "Google Ads – criminal defence",
      "Google Ads – conveyancing",
      "Practice area landing pages",
      "Legal SEO content",
      "Local SEO – city + practice",
      "Google Business Profile",
      "Avvo profile optimisation",
      "Martindale-Hubbell listing",
      "Post-matter review requests",
      "Reputation monitoring",
      "Click-to-call campaigns",
      "Legal FAQ schema markup",
      "Attorney bio authority pages",
      "YouTube legal explainers",
      "LinkedIn thought leadership",
      "AI Overview legal citations",
      "Retargeting website visitors",
      "Competitive conquesting ads",
    ],
    faq: [
      {
        q: "Do you understand legal advertising rules and restrictions?",
        a: "Yes. All campaigns follow ABA Model Rules and jurisdiction-specific advertising rules. We avoid prohibited solicitation tactics, ensure all disclaimers are present, and use language that complies with professional responsibility rules in each market.",
      },
      {
        q: "Do you work with all practice areas?",
        a: "Yes — personal injury, family law, criminal defence, immigration, employment, commercial law, intellectual property, real estate law, estate planning and more. Each practice area gets a tailored keyword strategy and landing page.",
      },
      {
        q: "What's the minimum budget for effective legal Google Ads?",
        a: "Legal is one of the most competitive Google Ads verticals. We recommend a minimum of $5,000–$8,000/month in ad spend for competitive practice areas like personal injury or criminal defence. Less competitive areas can perform well from $2,000–$3,000/month.",
      },
      {
        q: "Can you help a solo practitioner build their practice?",
        a: "Yes. Solo practitioners benefit significantly from our Local SEO and Google Business Profile strategy — these channels have lower costs than paid ads and can build a strong local reputation over 6–12 months.",
      },
    ],
    relatedSlugs: ["financial-advisor-marketing", "accounting-marketing", "coaching-consulting-marketing"],
  },

  /* ─────────────────────────────── FINANCIAL ADVISORS & WEALTH ─────────────────────────────── */
  {
    slug: "financial-advisor-marketing",
    name: "Financial Advisors & Wealth",
    shortName: "Financial",
    emoji: "📈",
    cover: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=85",
    group: "Legal & Finance",
    desc: "Attract high-net-worth clients with authority and trust marketing.",
    metaTitle: "Financial Advisor Marketing Agency — Attract HNW Clients with Google & LinkedIn | Lumotis",
    metaDescription: "Lumotis is a specialist financial advisor and wealth management marketing agency. We attract high-net-worth clients with Google Ads, LinkedIn, SEO and compliant content marketing. Free audit.",
    heroEyebrow: "Digital Marketing for Financial Advisors & Wealth Managers",
    heroHeadline: "Financial Marketing That Attracts Ideal Clients Who Trust You Before They Call",
    heroAccent: "More qualified prospects. Higher AUM. Referrals that compound.",
    heroTagline: "We build the compliant, authority-first marketing engine for financial advisors, wealth managers and financial planning firms — Google Ads, LinkedIn, SEO and thought leadership — consistently attracting high-net-worth clients who arrive pre-qualified.",
    heroStats: [
      { value: "+140%", label: "Avg qualified enquiries" },
      { value: "40+", label: "Advisory firms served" },
      { value: "$850k", label: "Avg AUM of new clients generated" },
    ],
    insightStats: [
      {
        value: "71%",
        label: "of HNW individuals research advisors online before making contact",
        desc: "Wealthy clients are thorough researchers. They read your content, check your credentials, review your case studies and assess your online reputation long before initiating contact. Your digital presence must instil trust at every step.",
      },
      {
        value: "3.8×",
        label: "higher lead quality from LinkedIn vs Facebook for financial advisors",
        desc: "LinkedIn's professional targeting — by income, job title, industry and seniority — consistently delivers higher-quality prospects for financial advisory firms. Most advisors underutilise this channel.",
      },
      {
        value: "85%",
        label: "of new financial advisor clients come via referral or trusted authority content",
        desc: "Digital marketing that builds genuine authority — educational content, thought leadership, verified credentials — mirrors the referral-driven trust dynamic that financial services relies on.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Financial Enquiries",
        body: "Compliant search campaigns targeting financial planning, wealth management, retirement planning and investment advice keywords. Landing pages built to demonstrate credentials, approach and client outcomes.",
      },
      {
        title: "LinkedIn Marketing for HNW Clients",
        body: "Sponsored content and lead generation campaigns on LinkedIn targeting business owners, senior executives and HNW individuals by income, title and industry. The most cost-effective channel for reaching ideal-profile clients.",
      },
      {
        title: "Financial SEO & Content Authority",
        body: "Retirement planning guides, investment explainers, market commentary and service pages that rank for financial research queries — establishing your advisors as the trusted voices clients want to work with.",
      },
      {
        title: "Reputation & Credential Building",
        body: "Google review generation, professional directory optimisation (NAPFA, CFP Board), and media placement strategy that reinforces credentials and builds the trust profile HNW clients require before engaging.",
      },
      {
        title: "Webinar & Lead Magnet Funnels",
        body: "Educational webinar campaigns, retirement planning calculator funnels and downloadable guide lead magnets — high-value content that attracts the right prospects and starts the advisory relationship on the right foot.",
      },
      {
        title: "Referral Marketing & Client Retention",
        body: "Structured referral incentive programmes, client event marketing and retention newsletters — keeping existing clients engaged and creating consistent word-of-mouth through your highest-trust channel.",
      },
    ],
    caseStudy: {
      metric: "+140%",
      metricLabel: "qualified enquiries per quarter",
      badge: "Financial · Singapore · LinkedIn + Google + Content",
      title: "Singapore Wealth Manager — doubled qualified enquiries from ideal HNW clients.",
      body: "A Singapore boutique wealth management firm relied entirely on referrals and had no digital presence. Lumotis built a LinkedIn sponsored content campaign targeting business owners and senior executives, launched retirement planning content SEO and deployed a webinar funnel for ideal prospects. 6-month result: qualified enquiries per quarter up 140%, average AUM of new enquiries at $1.2M, and the firm's LinkedIn page growing by 800 targeted followers.",
    },
    results: [
      { metric: "+140%", label: "qualified enquiries per quarter", market: "Singapore · Wealth Mgmt" },
      { metric: "$1.2M", label: "avg AUM of new enquiries", market: "LinkedIn + Google" },
      { metric: "+800", label: "targeted LinkedIn followers", market: "Thought leadership" },
      { metric: "3.8×", label: "ROI on marketing investment", market: "12-month engagement" },
    ],
    tactics: [
      "LinkedIn sponsored content",
      "LinkedIn lead generation forms",
      "Google Ads – financial planning",
      "Google Ads – retirement advice",
      "Wealth management SEO",
      "Retirement planning guides",
      "Investment explainer content",
      "Webinar funnel campaigns",
      "Lead magnet calculators",
      "Google Business Profile",
      "CFP / credential directory listings",
      "Google review generation",
      "Client newsletter campaigns",
      "Market commentary content",
      "YouTube financial education",
      "Facebook HNW retargeting",
      "AI Overview financial citations",
      "Referral incentive programme",
      "Client event marketing",
      "Podcast sponsorships",
    ],
    faq: [
      {
        q: "How do you ensure financial marketing is compliant with SEC/FCA/ASIC regulations?",
        a: "All ad copy and content is reviewed for compliance with relevant financial advertising regulations — SEC rules (USA), FCA guidelines (UK), ASIC (Australia) and MAS (Singapore). We avoid performance guarantees, include required disclaimers and can work with your compliance team for review.",
      },
      {
        q: "Do you work with RIAs, broker-dealers and independent advisors?",
        a: "Yes — across all financial advisory structures. We have playbooks for solo RIAs building a practice, multi-advisor firms scaling AUM and institutional wealth management teams building a digital brand.",
      },
      {
        q: "What's more effective for financial advisors — LinkedIn or Google Ads?",
        a: "LinkedIn delivers higher-quality prospects by demographic targeting precision. Google delivers higher intent (people actively searching for advice). The most effective strategy combines both — LinkedIn for awareness and education, Google for capturing active demand.",
      },
      {
        q: "How long does it take to build meaningful digital authority as a financial advisor?",
        a: "Paid channels (Google, LinkedIn) generate enquiries within 4–8 weeks. Content authority and SEO compound over 6–18 months. The combination builds a sustainable pipeline that becomes increasingly self-reinforcing over time.",
      },
    ],
    relatedSlugs: ["accounting-marketing", "law-firm-marketing", "mortgage-broker-marketing"],
  },

  /* ─────────────────────────────── ACCOUNTANTS & CPAS ─────────────────────────────── */
  {
    slug: "accounting-marketing",
    name: "Accountants & CPAs",
    shortName: "Accounting",
    emoji: "🧾",
    cover: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&q=85",
    group: "Legal & Finance",
    desc: "Attract business clients and individual tax filers at scale.",
    metaTitle: "Accounting Marketing Agency — Grow Your CPA Firm with Google Ads & Local SEO | Lumotis",
    metaDescription: "Lumotis is a specialist accounting and CPA marketing agency. We grow your client base with Google Ads, Local SEO, content marketing and referral programmes. Free audit.",
    heroEyebrow: "Digital Marketing for Accountants & CPAs",
    heroHeadline: "Accounting Marketing That Attracts Business Clients Who Stay for Years",
    heroAccent: "More business clients. Recurring revenue. A reputation that compounds.",
    heroTagline: "We build the full digital marketing engine for accountants, CPAs, tax advisors and bookkeeping firms — Google Ads, Local SEO, content marketing and referral programmes — consistently attracting high-value business clients with long retention curves.",
    heroStats: [
      { value: "+130%", label: "Avg new client enquiries" },
      { value: "50+", label: "Accounting firms served" },
      { value: "8.4yr", label: "Avg client retention for firms we serve" },
    ],
    insightStats: [
      {
        value: "78%",
        label: "of businesses search Google when looking for a new accountant",
        desc: "Business owners searching for accounting, tax preparation or bookkeeping services start with Google. Local SEO and Google Ads are the dominant acquisition channels for modern accounting firms.",
      },
      {
        value: "$12,000",
        label: "average lifetime value of a new SMB accounting client",
        desc: "A new business client acquired for $300–$500 in marketing cost is worth $12,000+ in lifetime fees. Marketing ROI in accounting is among the highest of any professional service.",
      },
      {
        value: "68%",
        label: "of accounting firms still rely solely on referrals for new business",
        desc: "Most accounting firms leave significant growth on the table by ignoring digital channels. Those who invest early capture market share that compounds over years as referrals build on top of digital leads.",
      },
    ],
    pillars: [
      {
        title: "Google Ads for Accounting Leads",
        body: "Search campaigns targeting business accounting, tax preparation, BAS/VAT, bookkeeping and financial planning keywords. Landing pages built for business owners with clear service outlines, pricing context and simple contact forms.",
      },
      {
        title: "Local SEO & Google Business Profile",
        body: "Rank #1 for 'accountant near me', 'CPA [city]', 'small business accountant [suburb]' and every accounting-related search in your market. GBP optimisation with professional photography and consistent posting.",
      },
      {
        title: "Industry Niche Content Marketing",
        body: "Tax guides, accounting tip articles, industry-specific financial advice and regulatory update content that attracts business owners from your target verticals — and establishes your firm as the specialist.",
      },
      {
        title: "Reputation & Review Management",
        body: "Post-engagement review requests, Google reputation monitoring and professional reply management. A strong review profile is a key differentiator in a market where most firms look identical online.",
      },
      {
        title: "Referral Programme & Client Retention",
        body: "Structured referral incentive programmes for existing clients, annual tax deadline reminder campaigns and relationship newsletter sequences that keep clients engaged and referring colleagues.",
      },
      {
        title: "LinkedIn & B2B Visibility",
        body: "LinkedIn content strategy and sponsored campaigns targeting business owners and CFOs with financial planning content, regulatory updates and firm expertise — building awareness before the next accountant search.",
      },
    ],
    caseStudy: {
      metric: "+130%",
      metricLabel: "new client enquiries per quarter",
      badge: "Accounting · Toronto · Google Ads + Local SEO + Content",
      title: "Toronto CPA Firm — doubled new business client enquiries in 5 months.",
      body: "A Toronto mid-sized CPA firm was entirely referral-dependent and had no Google visibility. Lumotis built Google Ads targeting SMB accounting keywords, optimised their GBP, and launched a content series targeting construction and real estate industry accounting — their two target verticals. 5-month result: new enquiries up 130%, average new client value at $8,200/year, and a #1 Google Maps ranking for 'accountant Toronto downtown'.",
    },
    results: [
      { metric: "+130%", label: "new client enquiries per quarter", market: "Toronto · CPA Firm" },
      { metric: "$8,200", label: "avg new client annual value", market: "SMB accounting" },
      { metric: "#1", label: "Google Maps – 'accountant Toronto'", market: "Local SEO" },
      { metric: "4.8★", label: "Google review average", market: "Reputation management" },
    ],
    tactics: [
      "Google Ads – SMB accounting",
      "Google Ads – tax preparation",
      "Google Ads – bookkeeping",
      "Local SEO – city keywords",
      "Google Business Profile",
      "Tax guide content marketing",
      "Industry niche content",
      "LinkedIn sponsored content",
      "LinkedIn business owner targeting",
      "Google review generation",
      "Referral incentive programme",
      "Tax deadline reminder emails",
      "Client retention newsletters",
      "BAS/VAT reminder automation",
      "Year-end planning campaigns",
      "Competitor conquesting ads",
      "Retargeting website visitors",
      "AI Overview accounting citations",
      "Schema markup – local business",
      "Professional directory listings",
    ],
    faq: [
      {
        q: "Do you work with individual CPAs and small bookkeeping firms?",
        a: "Yes. Solo CPAs and small firms benefit most from Local SEO and Google Ads — these channels have lower minimum spends and generate high-ROI local leads. We have packages sized for single-practitioner and boutique firms.",
      },
      {
        q: "Can you help us target a specific industry niche (e.g. real estate, construction)?",
        a: "Absolutely. Industry niche content marketing is one of our most effective accounting strategies. We create content and campaigns that speak directly to business owners in your target verticals, attracting clients who need specialist accounting knowledge.",
      },
      {
        q: "How do you manage the seasonal nature of accounting marketing (tax season peaks)?",
        a: "We plan campaigns around tax season with increased budget allocation and specific keyword targeting. Year-round campaigns keep a steady pipeline of new client enquiries flowing outside peak periods — so you're never scrambling.",
      },
      {
        q: "Do you work with international accounting firms or only domestic markets?",
        a: "We work in Australia, UK, USA, Canada and Singapore. Each market has different regulatory terminology and search behaviour — our campaigns are built with market-specific keyword research, not generic templates.",
      },
    ],
    relatedSlugs: ["financial-advisor-marketing", "law-firm-marketing", "mortgage-broker-marketing"],
  },
];

/* ─── Helpers ─────────────────────────────────────────────── */
export function getIndustryBySlug(slug) {
  return INDUSTRY_LIST.find((i) => i.slug === slug) || null;
}
